import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MediaSegment, TextOverlay, SegmentAnimIn, SegmentAnimOut, SegmentMotion } from '../types';
import { UGCVideoTimeline } from './UGCVideoTimeline';
import { UGCAddImagesStep } from './UGCAddImagesStep';
import { UGCEditorHelpTour, TourStep } from './UGCEditorHelpTour';
import { UGCTextTemplateGallery } from './UGCTextTemplateGallery';
import { MediaItem } from '../../../types/MediaItem';
import { UGC_BACKGROUND_MUSIC_PRESETS, UGC_MUSIC_PRESET_CUSTOM, UGC_TEXT_FONTS, UGC_DEFAULT_TEXT_FONT } from '../constants';
import { loadGoogleFont } from '../utils/loadGoogleFont';

interface UGCVideoEditorProps {
  generatedVideoUrl: string | null;
  baseVideoDurationSeconds: number | null;
  timelineSegments: MediaSegment[];
  onReorderSegments?: (fromIndex: number, toIndex: number) => void;
  onUpdateSegment?: (mediaId: string, updates: any) => void;
  onRemoveSegment?: (mediaId: string) => void;
  textOverlays: TextOverlay[];
  onAddTextOverlay?: (timeSec: number) => void;
  onUpdateTextOverlay?: (id: string, updates: Partial<TextOverlay>) => void;
  onRemoveTextOverlay?: (id: string) => void;
  onApplyEdits?: () => void;
  isApplyingEdits?: boolean;
  customerId: string | null;
  idToken: string;
  agentTools?: string[];
  scriptContent?: string;
  selectedMediaIds: string[];
  thumbnailById: Record<string, string>;
  segmentVideoUrls?: Record<string, string>;
  onToggleMedia: (mediaId: string, mediaType?: 'image' | 'video', insertAtTime?: number) => void;
  onClearAllMedia?: () => void;
  onMediaLoaded?: (items: MediaItem[]) => void;
  onVideoMediaImported?: (mediaId: string, videoUrl: string) => void;
  enableLibraryDrag?: boolean;
  captionsEnabled?: boolean;
  onCaptionsEnabledChange?: (v: boolean) => void;
  isAnalyzingCaptions?: boolean;
  /** Message shown in the Captions panel when auto-detection couldn't produce captions. */
  captionNotice?: string | null;
  /** Insert a ready-made text-frame template's overlay(s) at the given time. */
  onApplyTextTemplate?: (templateId: string, atTime: number) => void;
  musicPresetId?: string;
  onMusicPresetChange?: (v: string) => void;
  customMusicUrl?: string;
  onCustomMusicUrlChange?: (v: string) => void;
  musicVolumePercent?: number;
  onMusicVolumeChange?: (v: number) => void;
  avatarZoom?: number;
  onAvatarZoomChange?: (v: number) => void;
  /** Optional preview-only mirroring of HeyGen's avatar_layout so what's seen ≈ what ships. */
  avatarOffsetY?: number;
  avatarStyle?: 'normal' | 'closeUp' | 'circle';
  isTalkingPhotoAvatar?: boolean;
  avatarMatting?: boolean;
  videoFormat?: 'reel' | 'landscape' | 'square';
  hideHeader?: boolean;
}

const SEGMENT_ANIM_IN_OPTIONS: Array<{ value: SegmentAnimIn; label: string; title: string }> = [
  { value: 'none',        label: 'None',    title: 'No entry animation' },
  { value: 'fade',        label: 'Fade',    title: 'Fade in' },
  { value: 'slide-left',  label: '← Slide', title: 'Slide in from right, moves left' },
  { value: 'slide-right', label: '→ Slide', title: 'Slide in from left, moves right' },
  { value: 'slide-up',    label: '↑ Rise',  title: 'Rise up from bottom' },
  { value: 'slide-down',  label: '↓ Drop',  title: 'Drop in from top' },
  { value: 'wipe-left',   label: '← Wipe',  title: 'Wipe reveal left' },
  { value: 'wipe-right',  label: '→ Wipe',  title: 'Wipe reveal right' },
  { value: 'wipe-up',     label: '↑ Wipe',  title: 'Wipe reveal upward' },
  { value: 'wipe-down',   label: '↓ Wipe',  title: 'Wipe reveal downward' },
  { value: 'zoom-in',     label: 'Zoom In', title: 'Zoom in from center' },
  { value: 'pop',         label: 'Pop',     title: 'Pop in (elastic bounce)' },
  { value: 'spin',        label: 'Spin',    title: 'Spin into view' },
];

const SEGMENT_ANIM_OUT_OPTIONS: Array<{ value: SegmentAnimOut; label: string; title: string }> = [
  { value: 'none',        label: 'None',     title: 'No exit animation' },
  { value: 'fade',        label: 'Fade',     title: 'Fade out' },
  { value: 'slide-left',  label: '← Slide',  title: 'Slide out to left' },
  { value: 'slide-right', label: '→ Slide',  title: 'Slide out to right' },
  { value: 'slide-up',    label: '↑ Rise',   title: 'Rise out upward' },
  { value: 'slide-down',  label: '↓ Drop',   title: 'Drop out downward' },
  { value: 'wipe-left',   label: '← Wipe',   title: 'Wipe out left' },
  { value: 'wipe-right',  label: '→ Wipe',   title: 'Wipe out right' },
  { value: 'wipe-up',     label: '↑ Wipe',   title: 'Wipe out upward' },
  { value: 'wipe-down',   label: '↓ Wipe',   title: 'Wipe out downward' },
  { value: 'zoom-out',    label: 'Zoom Out', title: 'Zoom out' },
  { value: 'spin',        label: 'Spin',     title: 'Spin out' },
];

const SEGMENT_MOTION_OPTIONS: Array<{ value: SegmentMotion; label: string; title: string }> = [
  { value: 'none',          label: 'Static',  title: 'No motion (static image)' },
  { value: 'ken-burns-in',  label: 'KB ↗',   title: 'Ken Burns: slow zoom in + pan' },
  { value: 'ken-burns-out', label: 'KB ↙',   title: 'Ken Burns: slow zoom out + pan' },
  { value: 'pan-left',      label: '← Pan',  title: 'Pan right to left across clip' },
  { value: 'pan-right',     label: 'Pan →',  title: 'Pan left to right across clip' },
];

function getCaptionOverlayStyles(design: string, isSelected: boolean): { className: string; style: React.CSSProperties } {
  const ring = isSelected ? 'outline outline-2 outline-offset-4 outline-[#2f4941] rounded' : '';
  // No design applies its own background — backgroundColor is controlled independently.
  switch (design) {
    case 'minimalist':
      return {
        className: `relative px-3 py-1 font-medium text-white text-center ${ring}`,
        style: { textShadow: '0 1px 8px rgba(0,0,0,0.95), 0 2px 16px rgba(0,0,0,0.7)' },
      };
    case 'neon':
      return {
        className: `relative px-3 py-1 font-extrabold text-center ${ring}`,
        style: { color: '#39ff14', textShadow: '0 0 8px #39ff14, 0 0 20px #39ff14, 0 0 40px #39ff14' },
      };
    case 'bold':
      return {
        className: `relative px-3 py-1.5 font-black italic text-center ${ring}`,
        style: { color: '#ffe81a', textShadow: '0 2px 8px rgba(0,0,0,0.95), 0 1px 2px rgba(0,0,0,1)' },
      };
    case 'karaoke':
      return {
        className: `relative px-3 py-1 font-bold text-center ${ring}`,
        style: { color: '#FFE100', WebkitTextStroke: '0.6px #000', textShadow: '0 1px 4px rgba(0,0,0,0.95)' },
      };
    case 'fire':
      return {
        className: `relative px-3 py-1 font-extrabold text-center ${ring}`,
        style: { color: '#FF6B00', textShadow: '0 0 8px #FF6B00, 0 0 16px #FFA500, 0 2px 4px #7A0000' },
      };
    case 'shadow':
      return {
        className: `relative px-3 py-1 font-bold text-white text-center ${ring}`,
        style: { textShadow: '2px 2px 4px rgba(0,0,0,0.9), 4px 4px 12px rgba(0,0,0,0.7)' },
      };
    case 'gradient':
      return {
        className: `relative px-3 py-1 font-black text-center ${ring}`,
        style: { background: 'linear-gradient(90deg, #ff7a18, #af002d)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent', WebkitTextStroke: '0.5px rgba(0,0,0,0.8)', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))' },
      };
    case 'highlight':
      return {
        className: `relative px-3 py-1 font-black text-center ${ring}`,
        style: { color: '#FFE100', WebkitTextStroke: '0.8px #000', textShadow: '0 0 12px rgba(255,225,0,0.5)' },
      };
    case 'pill':
      return {
        className: `relative px-5 py-1.5 font-extrabold text-white text-center rounded-full ${ring}`,
        style: { textShadow: '0 1px 4px rgba(0,0,0,0.6)' },
      };
    case 'cinema':
      return {
        className: `relative px-3 py-1 font-black text-white text-center ${ring}`,
        style: { WebkitTextStroke: '1.2px #000', textShadow: '0 2px 6px rgba(0,0,0,0.85), 1px 1px 0 #000' },
      };
    case 'classic':
    default:
      return {
        className: `relative px-3 py-1 font-bold text-white text-center ${ring}`,
        style: { textShadow: '0 1px 3px rgba(0,0,0,1), 0 2px 12px rgba(0,0,0,0.95)' },
      };
  }
}

/** Animation progress easing helpers */
function easeOutCubic(t: number) { return 1 - Math.pow(1 - t, 3); }
function easeInCubic(t: number)  { return t * t * t; }
function easeOutElastic(t: number) {
  if (t === 0 || t === 1) return t;
  const c4 = (2 * Math.PI) / 3;
  return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
}

function _overlayDefaultInDur(anim: string) {
  return anim === 'pop' || anim === 'bounce' ? 0.55 : anim === 'typewriter' ? 0.8 : 0.4;
}

function _overlayInStyle(anim: string, p: number): React.CSSProperties {
  const e = easeOutCubic(p);
  switch (anim) {
    case 'fade':        return { opacity: e };
    case 'pop':         { const s = Math.max(0, easeOutElastic(p)); return { transform: `scale(${s})`, opacity: Math.min(1, p * 2) }; }
    case 'bounce':      { const s = Math.max(0, easeOutElastic(p)); return { transform: `scale(${0.3 + 0.7 * s})`, opacity: Math.min(1, p * 2) }; }
    case 'slide-up':    return { transform: `translateY(${(1 - e) * 36}px)`,  opacity: Math.min(1, p * 2.5) };
    case 'slide-down':  return { transform: `translateY(${-(1 - e) * 36}px)`, opacity: Math.min(1, p * 2.5) };
    case 'slide-left':  return { transform: `translateX(${(1 - e) * 60}px)`,  opacity: Math.min(1, p * 2.5) };
    case 'slide-right': return { transform: `translateX(${-(1 - e) * 60}px)`, opacity: Math.min(1, p * 2.5) };
    case 'wipe-up':     return { clipPath: `inset(${(1 - e) * 100}% 0 0 0)` };
    case 'wipe-down':   return { clipPath: `inset(0 0 ${(1 - e) * 100}% 0)` };
    case 'typewriter':  return { opacity: e };
    case 'spin':        return { transform: `rotate(${(1 - e) * -180}deg) scale(${0.1 + e * 0.9})`, opacity: e };
    default:            return { opacity: 1 };
  }
}

function _overlayOutStyle(anim: string, p: number): React.CSSProperties {
  const e = easeInCubic(p);
  switch (anim) {
    case 'fade':        return { opacity: 1 - e };
    case 'pop':         return { transform: `scale(${1 - e * 0.8})`, opacity: Math.max(0, 1 - p * 2) };
    case 'bounce':      return { transform: `scale(${1 - e * 0.8})`, opacity: Math.max(0, 1 - p * 2) };
    case 'slide-up':    return { transform: `translateY(${-e * 36}px)`,  opacity: 1 - e };
    case 'slide-down':  return { transform: `translateY(${e * 36}px)`,   opacity: 1 - e };
    case 'slide-left':  return { transform: `translateX(${-e * 60}px)`,  opacity: 1 - e };
    case 'slide-right': return { transform: `translateX(${e * 60}px)`,   opacity: 1 - e };
    case 'wipe-up':     return { clipPath: `inset(0 0 ${e * 100}% 0)` };
    case 'wipe-down':   return { clipPath: `inset(${e * 100}% 0 0 0)` };
    case 'spin':        return { transform: `rotate(${e * 180}deg) scale(${1 - e * 0.9})`, opacity: 1 - e };
    default:            return { opacity: 1 };
  }
}

/** Compute live animation style for a text overlay based on currentTime (in + out). */
function getOverlayAnimStyle(overlay: TextOverlay, currentTime: number): React.CSSProperties {
  // Default to no animation — preview must match the backend (which also defaults to 'none').
  const animIn  = overlay.animation     ?? 'none';
  const animOut = overlay.animation_out ?? 'none';
  if (animIn === 'none' && animOut === 'none') return {};
  const t = currentTime - overlay.time;
  if (t < 0) return { opacity: 0 };
  if (t >= overlay.duration) return { opacity: 0 };
  const inDur  = overlay.anim_duration     ?? _overlayDefaultInDur(animIn);
  const outDur = overlay.anim_out_duration ?? 0.35;
  const remaining = overlay.duration - t;
  // Exit window takes priority when both overlap (very short clips)
  if (animOut !== 'none' && remaining < outDur) {
    const p = Math.min(1, 1 - remaining / outDur);
    return { willChange: 'transform, opacity', ..._overlayOutStyle(animOut, p) };
  }
  if (animIn !== 'none' && t < inDur) {
    const p = Math.min(1, t / inDur);
    return { willChange: 'transform, opacity', ..._overlayInStyle(animIn, p) };
  }
  return { opacity: 1 };
}

const TEXT_DESIGN_OPTIONS: Array<{ value: NonNullable<TextOverlay['design']>; label: string; preview: { color: string; bg: string; stroke?: string } }> = [
  { value: 'classic',    label: 'Classic',    preview: { color: '#ffffff', bg: '#1f2937' } },
  { value: 'minimalist', label: 'Clean',      preview: { color: '#ffffff', bg: '#1f2937', stroke: '#000' } },
  { value: 'neon',       label: 'Neon',       preview: { color: '#39ff14', bg: '#1f2937' } },
  { value: 'bold',       label: 'Bold',       preview: { color: '#ffe81a', bg: '#1f2937' } },
  { value: 'karaoke',    label: 'Karaoke',    preview: { color: '#FFE100', bg: '#1f2937', stroke: '#000' } },
  { value: 'fire',       label: 'Fire',       preview: { color: '#FF6B00', bg: '#1f2937' } },
  { value: 'shadow',     label: 'Shadow',     preview: { color: '#ffffff', bg: '#1f2937' } },
  { value: 'gradient',   label: 'Gradient',   preview: { color: '#ff7a18', bg: '#1f2937' } },
  { value: 'highlight',  label: 'Marker',     preview: { color: '#FFE100', bg: '#1f2937', stroke: '#000' } },
  { value: 'pill',       label: 'Pill',       preview: { color: '#ffffff', bg: '#1f2937' } },
  { value: 'cinema',     label: 'Cinema',     preview: { color: '#ffffff', bg: '#1f2937', stroke: '#000' } },
];

const fmtTime = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const cs = Math.floor((sec % 1) * 100);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
};

const posToY = (position?: string) =>
  position === 'top' ? 12 : position === 'center' ? 50 : 80;

// ── Per-frame segment animation (computed from currentTime, mirrors Creatomate) ──

function _segInFrame(anim: string, p: number): React.CSSProperties {
  const e = easeOutCubic(p);
  switch (anim) {
    case 'fade':        return { opacity: e };
    case 'slide-left':  return { transform: `translateX(${(1-e)*110}%)`,  opacity: Math.min(1, p*2.5) };
    case 'slide-right': return { transform: `translateX(${-(1-e)*110}%)`, opacity: Math.min(1, p*2.5) };
    case 'slide-up':    return { transform: `translateY(${(1-e)*110}%)`,  opacity: Math.min(1, p*2.5) };
    case 'slide-down':  return { transform: `translateY(${-(1-e)*110}%)`, opacity: Math.min(1, p*2.5) };
    case 'wipe-left':   return { clipPath: `inset(0 ${(1-e)*100}% 0 0)` };
    case 'wipe-right':  return { clipPath: `inset(0 0 0 ${(1-e)*100}%)` };
    case 'wipe-up':     return { clipPath: `inset(${(1-e)*100}% 0 0 0)` };
    case 'wipe-down':   return { clipPath: `inset(0 0 ${(1-e)*100}% 0)` };
    case 'zoom-in':     return { transform: `scale(${0.05 + e*0.95})`, opacity: e };
    case 'pop':         { const s = Math.max(0, easeOutElastic(p)); return { transform: `scale(${s})`, opacity: Math.min(1, p*4) }; }
    case 'spin':        return { transform: `rotate(${(1-e)*-180}deg) scale(${0.05 + e*0.95})`, opacity: e };
    default:            return {};
  }
}

function _segOutFrame(anim: string, p: number): React.CSSProperties {
  const e = easeInCubic(p);
  switch (anim) {
    case 'fade':        return { opacity: 1-e };
    case 'slide-left':  return { transform: `translateX(${-e*110}%)`,  opacity: Math.max(0, 1-p*2.5) };
    case 'slide-right': return { transform: `translateX(${e*110}%)`,   opacity: Math.max(0, 1-p*2.5) };
    case 'slide-up':    return { transform: `translateY(${-e*110}%)`,  opacity: Math.max(0, 1-p*2.5) };
    case 'slide-down':  return { transform: `translateY(${e*110}%)`,   opacity: Math.max(0, 1-p*2.5) };
    case 'wipe-left':   return { clipPath: `inset(0 0 0 ${e*100}%)` };
    case 'wipe-right':  return { clipPath: `inset(0 ${e*100}% 0 0)` };
    case 'wipe-up':     return { clipPath: `inset(0 0 ${e*100}% 0)` };
    case 'wipe-down':   return { clipPath: `inset(${e*100}% 0 0 0)` };
    case 'zoom-out':    return { transform: `scale(${1-e*0.95})`, opacity: 1-e };
    case 'spin':        return { transform: `rotate(${e*180}deg) scale(${1-e*0.95})`, opacity: 1-e };
    default:            return {};
  }
}

/** Returns per-frame inline style for a segment based on currentTime. Empty object = no anim active. */
function getSegmentPlaybackStyle(seg: MediaSegment, t: number): React.CSSProperties {
  const animIn  = seg.animation_in;
  const animOut = seg.animation_out;
  const durIn   = Math.max(0.05, seg.anim_in_duration  ?? 0.5);
  const durOut  = Math.max(0.05, seg.anim_out_duration ?? 0.5);

  if (animIn && animIn !== 'none' && t >= seg.time && t < seg.time + durIn) {
    return _segInFrame(animIn, Math.min(1, (t - seg.time) / durIn));
  }
  const exitStart = seg.time + seg.duration - durOut;
  if (animOut && animOut !== 'none' && t >= exitStart && t < seg.time + seg.duration) {
    return _segOutFrame(animOut, Math.min(1, (t - exitStart) / durOut));
  }
  return {};
}

/** True when the segment is currently inside an entry or exit animation window. */
function isSegmentAnimating(seg: MediaSegment, t: number): boolean {
  const animIn  = seg.animation_in;
  const animOut = seg.animation_out;
  const durIn   = seg.anim_in_duration  ?? 0.5;
  const durOut  = seg.anim_out_duration ?? 0.5;
  if (animIn  && animIn  !== 'none' && t >= seg.time && t < seg.time + durIn) return true;
  if (animOut && animOut !== 'none' && t >= seg.time + seg.duration - durOut && t < seg.time + seg.duration) return true;
  return false;
}

// ── Animation preview helpers (CSS-transition based, no keyframe injection needed) ──

interface SegPreviewState {
  mediaId: string;
  animType: string;
  direction: 'in' | 'out';
  dur: number;
  phase: 'start' | 'end';
}

function buildAnimTransition(dur: number): string {
  const d = `${dur}s`;
  return `transform ${d} cubic-bezier(0.25,0.1,0.25,1), opacity ${Math.min(dur * 0.8, 0.5)}s ease-out, clip-path ${d} cubic-bezier(0.25,0.1,0.25,1)`;
}

function getSegAnimPreviewStyle(preview: SegPreviewState): React.CSSProperties {
  const { animType, direction, phase, dur } = preview;
  const T = buildAnimTransition(dur);

  if (direction === 'in') {
    // Entry: start = off-screen/invisible → end = natural
    if (phase === 'end') {
      const needsClip = animType.startsWith('wipe-');
      return { transform: undefined, opacity: 1, ...(needsClip ? { clipPath: 'inset(0 0 0 0)' } : {}), transition: T };
    }
    switch (animType) {
      case 'fade':        return { opacity: 0, transition: T };
      case 'slide-left':  return { transform: 'translateX(110%)', opacity: 0.4, transition: T };
      case 'slide-right': return { transform: 'translateX(-110%)', opacity: 0.4, transition: T };
      case 'slide-up':    return { transform: 'translateY(110%)', opacity: 0.4, transition: T };
      case 'slide-down':  return { transform: 'translateY(-110%)', opacity: 0.4, transition: T };
      case 'wipe-left':   return { clipPath: 'inset(0 100% 0 0)', transition: T };
      case 'wipe-right':  return { clipPath: 'inset(0 0 0 100%)', transition: T };
      case 'wipe-up':     return { clipPath: 'inset(100% 0 0 0)', transition: T };
      case 'wipe-down':   return { clipPath: 'inset(0 0 100% 0)', transition: T };
      case 'zoom-in':     return { transform: 'scale(0.05)', opacity: 0, transition: T };
      case 'pop':         return { transform: 'scale(0.25)', opacity: 0, transition: T };
      case 'spin':        return { transform: 'rotate(-180deg) scale(0.1)', opacity: 0, transition: T };
      default:            return { opacity: 0, transition: T };
    }
  } else {
    // Exit: start = natural/visible → end = off-screen/invisible
    const Te = buildAnimTransition(dur).replace('ease-out', 'ease-in');
    if (phase === 'start') {
      const needsClip = animType.startsWith('wipe-');
      return { transform: undefined, opacity: 1, ...(needsClip ? { clipPath: 'inset(0 0 0 0)' } : {}), transition: Te };
    }
    switch (animType) {
      case 'fade':        return { opacity: 0, transition: Te };
      case 'slide-left':  return { transform: 'translateX(-110%)', opacity: 0.4, transition: Te };
      case 'slide-right': return { transform: 'translateX(110%)', opacity: 0.4, transition: Te };
      case 'slide-up':    return { transform: 'translateY(-110%)', opacity: 0.4, transition: Te };
      case 'slide-down':  return { transform: 'translateY(110%)', opacity: 0.4, transition: Te };
      case 'wipe-left':   return { clipPath: 'inset(0 0 0 100%)', transition: Te };
      case 'wipe-right':  return { clipPath: 'inset(0 100% 0 0)', transition: Te };
      case 'wipe-up':     return { clipPath: 'inset(0 0 100% 0)', transition: Te };
      case 'wipe-down':   return { clipPath: 'inset(100% 0 0 0)', transition: Te };
      case 'zoom-out':    return { transform: 'scale(0.05)', opacity: 0, transition: Te };
      case 'spin':        return { transform: 'rotate(180deg) scale(0.1)', opacity: 0, transition: Te };
      default:            return { opacity: 0, transition: Te };
    }
  }
}

const EDITOR_TOUR_STEPS: TourStep[] = [
  { target: 'play',     title: 'Preview your video', description: 'Press play (or the spacebar) to watch the avatar with your overlays, clips, music and captions exactly as they’ll render.' },
  { target: 'text',     title: 'Add text', description: 'Drops an editable text box on the canvas. Double-click to type, then drag, resize, rotate, or restyle it.' },
  { target: 'templates', title: 'Text templates', description: 'Pick a ready-made text design — titles, lower-thirds, quotes, CTAs, animated text — and it drops onto the canvas ready to edit.' },
  { target: 'media',    title: 'Add image or video', description: 'Insert a photo or stock clip at the playhead. It lands on the canvas already selected so you can drag and resize it right away.' },
  { target: 'music',    title: 'Background music', description: 'Pick a track or paste a URL, then balance its volume against the voiceover.' },
  { target: 'captions', title: 'Captions', description: 'Generate captions from your script — accurately timed and matching the spoken words — and fine-tune them as editable text.' },
  { target: 'delete',   title: 'Delete', description: 'Removes whatever is selected — a clip or a text overlay. Delete/Backspace works too.' },
  { target: 'timeline', title: 'Timeline', description: 'Scrub, reorder clips, and fine-tune the timing of every overlay and segment. Overlapping clips stack onto their own lanes so each stays easy to grab.' },
];

export const UGCVideoEditor: React.FC<UGCVideoEditorProps> = ({
  generatedVideoUrl,
  baseVideoDurationSeconds,
  timelineSegments,
  onReorderSegments,
  onUpdateSegment,
  onRemoveSegment,
  textOverlays,
  onAddTextOverlay,
  onUpdateTextOverlay,
  onRemoveTextOverlay,
  onApplyEdits,
  isApplyingEdits,
  customerId,
  idToken,
  agentTools,
  scriptContent,
  selectedMediaIds,
  thumbnailById,
  segmentVideoUrls = {},
  onToggleMedia,
  onClearAllMedia,
  onMediaLoaded,
  onVideoMediaImported,
  enableLibraryDrag,
  captionsEnabled = false,
  onCaptionsEnabledChange,
  isAnalyzingCaptions = false,
  captionNotice = null,
  onApplyTextTemplate,
  musicPresetId = 'none',
  onMusicPresetChange,
  customMusicUrl = '',
  onCustomMusicUrlChange,
  musicVolumePercent = 30,
  onMusicVolumeChange,
  avatarZoom = 1.0,
  onAvatarZoomChange,
  avatarOffsetY = 0,
  avatarStyle = 'normal',
  isTalkingPhotoAvatar = false,
  avatarMatting = false,
  videoFormat = 'reel',
  hideHeader = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const musicAudioRef = useRef<HTMLAudioElement | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [previewingPresetId, setPreviewingPresetId] = useState<string | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaModalTab, setMediaModalTab] = useState<'library' | 'videos' | 'upload' | 'ai' | 'music' | 'templates'>('library');
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [overlayPositions, setOverlayPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [editingOverlayId, setEditingOverlayId] = useState<string | null>(null);
  // "Apply style to all" button: a brief loading state so the click feels deliberate,
  // then an inline confirmation. Both cleared by timeouts.
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [applyAllAck, setApplyAllAck] = useState<number | null>(null);
  const shouldAutoEditNewOverlayRef = useRef(false);
  const prevOverlayCountRef = useRef(textOverlays.length);
  const [segPreview, setSegPreview] = useState<SegPreviewState | null>(null);
  const segPreviewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [segmentTransforms, setSegmentTransforms] = useState<Record<string, { x: number; y: number; width: number; height: number }>>({});
  // Canvas zoom: scales the whole stage (video + all overlays) for precise overlay editing.
  // Gesture math is unaffected — handlers measure via getBoundingClientRect(), which scales
  // with the CSS transform; only the render-time font calc must use unscaled layout dims.
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const canvasZoomRef = useRef(1);
  const canvasViewportRef = useRef<HTMLDivElement>(null);
  const didCanvasPanRef = useRef(false);
  const editorRootRef = useRef<HTMLDivElement>(null);
  const [showHelpTour, setShowHelpTour] = useState(false);
  // Mirror of the text-overlay auto-edit flow: when media is added, auto-select the
  // new clip on the canvas (handles active) so it behaves just like a new text box.
  const shouldSelectNewSegmentRef = useRef(false);
  const prevSegmentCountRef = useRef(timelineSegments.length);

  // Resolved background music URL for canvas overlay
  const resolvedMusicUrl = musicPresetId === UGC_MUSIC_PRESET_CUSTOM
    ? (customMusicUrl?.trim() ?? '')
    : (UGC_BACKGROUND_MUSIC_PRESETS.find(p => p.id === musicPresetId)?.url?.trim() ?? '');

  // Sync background music with video play/pause/seek
  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio) return;
    if (!resolvedMusicUrl) { audio.pause(); audio.src = ''; return; }
    if (audio.src !== resolvedMusicUrl) { audio.src = resolvedMusicUrl; audio.loop = true; }
    audio.volume = Math.min(1, Math.max(0, (musicVolumePercent ?? 30) / 100));
  }, [resolvedMusicUrl, musicVolumePercent]);

  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio || !resolvedMusicUrl) return;
    if (isPlaying) { audio.play().catch(() => {}); }
    else { audio.pause(); }
  }, [isPlaying, resolvedMusicUrl]);

  // Stop preview audio when modal closes
  useEffect(() => {
    if (!showMediaModal) {
      previewAudioRef.current?.pause();
      setPreviewingPresetId(null);
      setPreviewLoadingId(null);
    }
  }, [showMediaModal]);

  // Preload all curated fonts (so the picker dropdown previews each option in its
  // own face) plus any fonts already chosen by existing overlays.
  useEffect(() => {
    UGC_TEXT_FONTS.forEach(f => loadGoogleFont(f.family));
  }, []);
  useEffect(() => {
    textOverlays.forEach(t => loadGoogleFont(t.fontFamily));
  }, [textOverlays]);

  useEffect(() => {
    if (shouldAutoEditNewOverlayRef.current && textOverlays.length > prevOverlayCountRef.current) {
      const newOverlay = textOverlays[textOverlays.length - 1];
      if (newOverlay) {
        if (newOverlay.groupId) {
          // Multi-design template: select the composition (first member) without forcing
          // edit mode, so the whole design is visible. Each part stays double-click editable.
          const first = textOverlays.find(o => o.groupId === newOverlay.groupId) ?? newOverlay;
          setSelectedOverlayId(first.id);
        } else {
          setSelectedOverlayId(newOverlay.id);
          setEditingOverlayId(newOverlay.id);
        }
        setSelectedSegmentId(null);
      }
      shouldAutoEditNewOverlayRef.current = false;
    }
    prevOverlayCountRef.current = textOverlays.length;
  }, [textOverlays]);

  const handleAddTextOverlay = useCallback(() => {
    shouldAutoEditNewOverlayRef.current = true;
    onAddTextOverlay?.(currentTime);
  }, [onAddTextOverlay, currentTime]);

  // When a clip is added from the media modal, select it on the canvas, give it a
  // default entry animation, seek to it, and close the modal — parity with text.
  useEffect(() => {
    if (shouldSelectNewSegmentRef.current && timelineSegments.length > prevSegmentCountRef.current) {
      const newSeg = timelineSegments[timelineSegments.length - 1];
      if (newSeg) {
        setSelectedSegmentId(newSeg.media_id);
        setSelectedOverlayId(null);
        if (!newSeg.animation_in || newSeg.animation_in === 'none') {
          onUpdateSegment?.(newSeg.media_id, { animation_in: 'fade', anim_in_duration: 0.5 });
        }
        // Move the playhead onto the new clip so it's visible with handles.
        if (videoRef.current && (currentTime < newSeg.time || currentTime >= newSeg.time + newSeg.duration)) {
          videoRef.current.currentTime = newSeg.time;
          setCurrentTime(newSeg.time);
        }
      }
      shouldSelectNewSegmentRef.current = false;
      setShowMediaModal(false);
    }
    prevSegmentCountRef.current = timelineSegments.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineSegments]);

  const handleAddMediaFromModal = useCallback((mediaId: string, mediaType?: 'image' | 'video') => {
    shouldSelectNewSegmentRef.current = true;
    onToggleMedia(mediaId, mediaType, currentTime);
  }, [onToggleMedia, currentTime]);

  const handleApplyTextTemplate = useCallback((templateId: string) => {
    // Mirror the text-overlay add flow: auto-select + edit the inserted overlay(s).
    shouldAutoEditNewOverlayRef.current = true;
    onApplyTextTemplate?.(templateId, currentTime);
    setShowMediaModal(false);
  }, [onApplyTextTemplate, currentTime]);

  const triggerAnimPreview = useCallback((seg: MediaSegment, animType: string, direction: 'in' | 'out') => {
    if (!animType || animType === 'none') return;
    const dur = direction === 'in'
      ? (seg.anim_in_duration ?? 0.5)
      : (seg.anim_out_duration ?? 0.5);
    if (segPreviewTimerRef.current) clearTimeout(segPreviewTimerRef.current);
    // Seek so segment is visible on canvas
    if (videoRef.current) {
      videoRef.current.currentTime = seg.time;
      setCurrentTime(seg.time);
    }
    const base: SegPreviewState = { mediaId: seg.media_id, animType, direction, dur, phase: 'start' };
    setSegPreview(base);
    // Two rAFs so the browser paints the start style before applying the end transition
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setSegPreview(p => p ? { ...p, phase: 'end' } : null);
    }));
    segPreviewTimerRef.current = setTimeout(() => setSegPreview(null), dur * 1000 + 400);
  }, []);

  const duration = baseVideoDurationSeconds ?? 60;
  const getOverlayPos = useCallback((overlay: TextOverlay) =>
    overlayPositions[overlay.id] ??
    (overlay.x_pct !== undefined && overlay.y_pct !== undefined
      ? { x: overlay.x_pct, y: overlay.y_pct }
      : { x: 50, y: posToY(overlay.position) }),
  [overlayPositions]);

  // Overlays sharing a groupId (e.g. a multi-design template) act as one unit: move,
  // select, and delete together. Returns just the overlay itself when ungrouped.
  const getGroupMembers = useCallback((overlayId: string): TextOverlay[] => {
    const o = textOverlays.find(t => t.id === overlayId);
    if (!o) return [];
    if (!o.groupId) return [o];
    return textOverlays.filter(t => t.groupId === o.groupId);
  }, [textOverlays]);

  const removeOverlayOrGroup = useCallback((overlayId: string) => {
    getGroupMembers(overlayId).forEach(m => onRemoveTextOverlay?.(m.id));
  }, [getGroupMembers, onRemoveTextOverlay]);

  // Drag carries every group member's start position so they move as one.
  const activeDragRef = useRef<{ id: string; containerRect: DOMRect; startMX: number; startMY: number; members: Array<{ id: string; startX: number; startY: number }> } | null>(null);
  const activeResizeRef = useRef<{
    id: string;
    startMX: number; startMY: number;
    startFontSize: number; startWidth: number;
    containerMin: number; containerW: number;
    mode: 'font' | 'width' | 'both';
    hDir: 1 | -1; vDir: 1 | -1;
    // When the overlay belongs to a group, resizing scales the whole group about its centre.
    group?: { cx: number; cy: number; members: Array<{ id: string; fontSize: number; width: number; x: number; y: number }> };
  } | null>(null);
  const activeRotateRef = useRef<{ id: string; centerX: number; centerY: number; startAngle: number; startRotation: number } | null>(null);
  const activeSegmentDragRef = useRef<{ id: string; containerRect: DOMRect; startMX: number; startMY: number; startX: number; startY: number } | null>(null);
  const activeSegmentResizeRef = useRef<{ id: string; containerRect: DOMRect; startMX: number; startMY: number; startX: number; startY: number; startW: number; startH: number; handle: 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w' } | null>(null);

  const handleOverlayResizeDown = useCallback((
    e: React.PointerEvent,
    overlay: TextOverlay,
    mode: 'font' | 'width' | 'both',
    hDir: 1 | -1 = 1,
    vDir: 1 | -1 = 1,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const container = videoContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const containerMin = Math.min(rect.width, rect.height);
    const fontOf = (o: TextOverlay) => o.fontSize ?? (o.style === 'title' ? 8 : o.style === 'subtitle' ? 5.5 : 4.5);
    const startFontSize = fontOf(overlay);
    const startWidth = overlay.width ?? 56;
    // If grouped, capture every member's start state + the group centre so the whole
    // composition scales together (keeps spacing + proportions).
    let group: { cx: number; cy: number; members: Array<{ id: string; fontSize: number; width: number; x: number; y: number }> } | undefined;
    const members = getGroupMembers(overlay.id);
    if (members.length > 1) {
      const ms = members.map(m => {
        const p = overlayPositions[m.id] ??
          (m.x_pct !== undefined && m.y_pct !== undefined ? { x: m.x_pct, y: m.y_pct } : { x: 50, y: posToY(m.position) });
        return { id: m.id, fontSize: fontOf(m), width: m.width ?? 56, x: p.x, y: p.y };
      });
      const cx = ms.reduce((s, m) => s + m.x, 0) / ms.length;
      const cy = ms.reduce((s, m) => s + m.y, 0) / ms.length;
      group = { cx, cy, members: ms };
    }
    activeResizeRef.current = {
      id: overlay.id,
      startMX: e.clientX, startMY: e.clientY,
      startFontSize, startWidth,
      containerMin, containerW: rect.width,
      mode, hDir, vDir, group,
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [getGroupMembers, overlayPositions]);

  const handleOverlayResizeMove = useCallback((e: React.PointerEvent) => {
    const r = activeResizeRef.current;
    if (!r) return;
    e.preventDefault();

    // Grouped overlay → scale the whole composition about its centre (font + width + spacing).
    if (r.group) {
      let scale: number;
      if (r.mode === 'width') {
        const dxPct = ((e.clientX - r.startMX) / r.containerW) * 100 * r.hDir;
        scale = (r.startWidth + dxPct) / r.startWidth;
      } else {
        const dyVmin = ((e.clientY - r.startMY) * r.vDir / r.containerMin) * 100 * 0.5;
        scale = (r.startFontSize + dyVmin) / r.startFontSize;
      }
      scale = Math.max(0.3, Math.min(3, scale));
      const { cx, cy, members } = r.group;
      setOverlayPositions(prev => {
        const next = { ...prev };
        members.forEach(m => {
          next[m.id] = {
            x: Math.max(5, Math.min(95, cx + (m.x - cx) * scale)),
            y: Math.max(5, Math.min(92, cy + (m.y - cy) * scale)),
          };
        });
        return next;
      });
      members.forEach(m => onUpdateTextOverlay?.(m.id, {
        fontSize: Math.round(Math.max(2.5, Math.min(20, m.fontSize * scale)) * 10) / 10,
        width: Math.round(Math.max(15, Math.min(95, m.width * scale)) * 10) / 10,
      }));
      return;
    }

    const updates: Partial<TextOverlay> = {};
    if (r.mode === 'width' || r.mode === 'both') {
      const dxPct = ((e.clientX - r.startMX) / r.containerW) * 100 * r.hDir;
      updates.width = Math.round(Math.max(15, Math.min(90, r.startWidth + dxPct)) * 10) / 10;
    }
    if (r.mode === 'font' || r.mode === 'both') {
      const dyVmin = ((e.clientY - r.startMY) * r.vDir / r.containerMin) * 100 * 0.5;
      updates.fontSize = Math.round(Math.max(2.5, Math.min(20, r.startFontSize + dyVmin)) * 10) / 10;
    }
    onUpdateTextOverlay?.(r.id, updates);
  }, [onUpdateTextOverlay]);

  const handleOverlayResizeUp = useCallback((e: React.PointerEvent) => {
    const r = activeResizeRef.current;
    if (!r) return;
    // Persist the scaled positions of every group member.
    if (r.group) {
      r.group.members.forEach(m => {
        const pos = overlayPositions[m.id];
        if (pos) onUpdateTextOverlay?.(m.id, { x_pct: pos.x, y_pct: pos.y });
      });
    }
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    activeResizeRef.current = null;
  }, [overlayPositions, onUpdateTextOverlay]);

  const handleOverlayRotateDown = useCallback((e: React.PointerEvent, overlay: TextOverlay) => {
    e.preventDefault();
    e.stopPropagation();
    const container = videoContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const pos = overlayPositions[overlay.id] ??
      (overlay.x_pct !== undefined && overlay.y_pct !== undefined
        ? { x: overlay.x_pct, y: overlay.y_pct }
        : { x: 50, y: posToY(overlay.position) });
    const centerX = rect.left + (pos.x / 100) * rect.width;
    const centerY = rect.top + (pos.y / 100) * rect.height;
    const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    activeRotateRef.current = { id: overlay.id, centerX, centerY, startAngle, startRotation: overlay.rotation ?? 0 };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [overlayPositions]);

  const handleOverlayRotateMove = useCallback((e: React.PointerEvent) => {
    const r = activeRotateRef.current;
    if (!r) return;
    e.preventDefault();
    const angle = Math.atan2(e.clientY - r.centerY, e.clientX - r.centerX) * (180 / Math.PI);
    const delta = angle - r.startAngle;
    const newRot = Math.round(((r.startRotation + delta) % 360 + 360) % 360);
    onUpdateTextOverlay?.(r.id, { rotation: newRot });
  }, [onUpdateTextOverlay]);

  const handleOverlayRotateUp = useCallback((e: React.PointerEvent) => {
    if (!activeRotateRef.current) return;
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
    activeRotateRef.current = null;
  }, []);

  const handleOverlayPointerDown = useCallback((e: React.PointerEvent, overlayId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedOverlayId(overlayId);
    setSelectedSegmentId(null);
    const container = videoContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    // Capture the start position of every member so the group drags together.
    const members = getGroupMembers(overlayId).map(m => {
      const p = overlayPositions[m.id] ??
        (m.x_pct !== undefined && m.y_pct !== undefined
          ? { x: m.x_pct, y: m.y_pct }
          : { x: 50, y: posToY(m.position) });
      return { id: m.id, startX: p.x, startY: p.y };
    });
    activeDragRef.current = { id: overlayId, containerRect: rect, startMX: e.clientX, startMY: e.clientY, members };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [getGroupMembers, overlayPositions]);

  const handleOverlayPointerMove = useCallback((e: React.PointerEvent, overlayId: string) => {
    const d = activeDragRef.current;
    if (!d || d.id !== overlayId) return;
    const dx = ((e.clientX - d.startMX) / d.containerRect.width) * 100;
    const dy = ((e.clientY - d.startMY) / d.containerRect.height) * 100;
    setOverlayPositions(prev => {
      const next = { ...prev };
      d.members.forEach(m => {
        next[m.id] = {
          x: Math.max(5, Math.min(95, m.startX + dx)),
          y: Math.max(5, Math.min(92, m.startY + dy)),
        };
      });
      return next;
    });
  }, []);

  const handleOverlayPointerUp = useCallback((_e: React.PointerEvent, overlayId: string) => {
    const d = activeDragRef.current;
    if (!d || d.id !== overlayId) return;
    activeDragRef.current = null;
    d.members.forEach(m => {
      const pos = overlayPositions[m.id] ?? { x: m.startX, y: m.startY };
      const posEnum: 'top' | 'center' | 'bottom' = pos.y < 33 ? 'top' : pos.y < 66 ? 'center' : 'bottom';
      onUpdateTextOverlay?.(m.id, { position: posEnum, x_pct: pos.x, y_pct: pos.y });
    });
  }, [overlayPositions, onUpdateTextOverlay]);

  const handleSegmentDragStart = useCallback((e: React.PointerEvent, segmentId: string) => {
    e.preventDefault();
    e.stopPropagation();
    // Take full focus immediately on press (mirrors handleOverlayPointerDown): select this
    // segment and clear any text-overlay selection so only one set of chrome shows.
    setSelectedSegmentId(segmentId);
    setSelectedOverlayId(null);
    const container = videoContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const current = segmentTransforms[segmentId] ?? { x: 0, y: 0, width: 100, height: 100 };
    activeSegmentDragRef.current = { id: segmentId, containerRect: rect, startMX: e.clientX, startMY: e.clientY, startX: current.x, startY: current.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [segmentTransforms]);

  const handleSegmentDragMove = useCallback((e: React.PointerEvent, segmentId: string) => {
    const d = activeSegmentDragRef.current;
    if (!d || d.id !== segmentId) return;
    e.preventDefault();
    const dx = ((e.clientX - d.startMX) / d.containerRect.width) * 100;
    const dy = ((e.clientY - d.startMY) / d.containerRect.height) * 100;
    const current = segmentTransforms[segmentId] ?? { x: 0, y: 0, width: 100, height: 100 };
    const newX = Math.max(0, Math.min(100 - current.width, d.startX + dx));
    const newY = Math.max(0, Math.min(100 - current.height, d.startY + dy));
    setSegmentTransforms(prev => ({
      ...prev,
      [segmentId]: { ...current, x: newX, y: newY }
    }));
  }, [segmentTransforms]);

  /** Persist the canvas layout onto the segment so it survives the editor and reaches the
   *  assembly payload — without this the placement is preview-only and the render ignores it. */
  const persistSegmentTransform = useCallback((segmentId: string) => {
    const t = segmentTransforms[segmentId];
    if (!t) return;
    onUpdateSegment?.(segmentId, {
      x_pct: Math.round(t.x * 10) / 10,
      y_pct: Math.round(t.y * 10) / 10,
      width_pct: Math.round(t.width * 10) / 10,
      height_pct: Math.round(t.height * 10) / 10,
    });
  }, [segmentTransforms, onUpdateSegment]);

  const handleSegmentDragEnd = useCallback((_e: React.PointerEvent, segmentId: string) => {
    if (activeSegmentDragRef.current?.id !== segmentId) return;
    activeSegmentDragRef.current = null;
    persistSegmentTransform(segmentId);
  }, [persistSegmentTransform]);

  const handleSegmentResizeStart = useCallback((e: React.PointerEvent, segmentId: string, handle: 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w') => {
    e.preventDefault();
    e.stopPropagation();
    const container = videoContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const current = segmentTransforms[segmentId] ?? { x: 0, y: 0, width: 100, height: 100 };
    activeSegmentResizeRef.current = {
      id: segmentId,
      containerRect: rect,
      startMX: e.clientX,
      startMY: e.clientY,
      startX: current.x,
      startY: current.y,
      startW: current.width,
      startH: current.height,
      handle
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [segmentTransforms]);

  const handleSegmentResizeMove = useCallback((e: React.PointerEvent) => {
    const r = activeSegmentResizeRef.current;
    if (!r) return;
    e.preventDefault();
    const dx = ((e.clientX - r.startMX) / r.containerRect.width) * 100;
    const dy = ((e.clientY - r.startMY) / r.containerRect.height) * 100;
    const { handle, startX, startY, startW, startH } = r;
    let newX = startX, newY = startY, newW = startW, newH = startH;

    if (handle.includes('w')) { newX = startX + dx; newW = Math.max(10, startW - dx); }
    if (handle.includes('e')) { newW = Math.max(10, startW + dx); }
    if (handle.includes('n')) { newY = startY + dy; newH = Math.max(10, startH - dy); }
    if (handle.includes('s')) { newH = Math.max(10, startH + dy); }

    newX = Math.max(0, Math.min(100, newX));
    newY = Math.max(0, Math.min(100, newY));
    newW = Math.min(100, newW);
    newH = Math.min(100, newH);

    setSegmentTransforms(prev => ({
      ...prev,
      [r.id]: { x: newX, y: newY, width: newW, height: newH }
    }));
  }, []);

  const handleSegmentResizeEnd = useCallback((_e: React.PointerEvent) => {
    const id = activeSegmentResizeRef.current?.id;
    activeSegmentResizeRef.current = null;
    if (id) persistSegmentTransform(id);
  }, [persistSegmentTransform]);

  // Restore saved canvas layouts (e.g. from a draft) into local transform state once per clip.
  useEffect(() => {
    setSegmentTransforms(prev => {
      let changed = false;
      const next = { ...prev };
      for (const seg of timelineSegments) {
        if (next[seg.media_id]) continue;
        if (seg.x_pct !== undefined || seg.y_pct !== undefined || seg.width_pct !== undefined || seg.height_pct !== undefined) {
          next[seg.media_id] = {
            x: seg.x_pct ?? 0,
            y: seg.y_pct ?? 0,
            width: seg.width_pct ?? 100,
            height: seg.height_pct ?? 100,
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [timelineSegments]);

  const setCanvasZoomClamped = useCallback((next: number) => {
    const clamped = Math.max(0.5, Math.min(2, Math.round(next * 100) / 100));
    canvasZoomRef.current = clamped;
    setCanvasZoom(clamped);
    // Pan only makes sense while zoomed in; recentre when back at/below 100%.
    if (clamped <= 1) setCanvasPan({ x: 0, y: 0 });
  }, []);

  // Ctrl/Cmd + scroll zooms the canvas. Native non-passive listener — React's synthetic
  // wheel handlers are passive, so preventDefault (to stop browser page-zoom) needs this.
  useEffect(() => {
    const vp = canvasViewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      setCanvasZoomClamped(canvasZoomRef.current * (e.deltaY < 0 ? 1.1 : 0.9));
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [setCanvasZoomClamped]);

  /** Drag empty canvas background to pan while zoomed in (overlay/segment drags are untouched —
   *  this only engages when the pointer goes down on the viewport itself). */
  const handleCanvasPanPointerDown = useCallback((e: React.PointerEvent) => {
    if (canvasZoomRef.current <= 1) return;
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    const start = { mx: e.clientX, my: e.clientY, px: canvasPan.x, py: canvasPan.y };
    didCanvasPanRef.current = false;
    const onMove = (me: PointerEvent) => {
      const dx = me.clientX - start.mx;
      const dy = me.clientY - start.my;
      if (!didCanvasPanRef.current && Math.hypot(dx, dy) < 3) return;
      didCanvasPanRef.current = true;
      setCanvasPan({ x: start.px + dx, y: start.py + dy });
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [canvasPan]);

  // While playing, sync currentTime on every frame so text animations are smooth (otherwise
  // we only update ~4×/sec from the video's onTimeUpdate event, causing choppy entry/exit).
  useEffect(() => {
    if (!isPlaying) return;
    let raf = 0;
    const tick = () => {
      const v = videoRef.current;
      if (v) setCurrentTime(v.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement) && !(e.target instanceof HTMLButtonElement)) {
        e.preventDefault();
        const v = videoRef.current;
        if (!v) return;
        if (v.paused || v.ended) { if (v.ended) v.currentTime = 0; v.play().catch(() => {}); }
        else v.pause();
        return;
      }
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedOverlayId) { removeOverlayOrGroup(selectedOverlayId); setSelectedOverlayId(null); }
        else if (selectedSegmentId) { onRemoveSegment?.(selectedSegmentId); setSelectedSegmentId(null); }
      }
      if (e.key === 'Escape') { setEditingOverlayId(null); setSelectedOverlayId(null); setSelectedSegmentId(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPlaying, selectedOverlayId, selectedSegmentId, removeOverlayOrGroup, onRemoveSegment]);

  const selectedOverlay = textOverlays.find(o => o.id === selectedOverlayId);
  const selectedSegment = timelineSegments.find(s => s.media_id === selectedSegmentId);
  const activeSegments = timelineSegments.filter(s => currentTime >= s.time && currentTime < s.time + s.duration);

  const handleSelectSegment = useCallback((segmentId: string) => {
    setSelectedSegmentId(segmentId);
    setSelectedOverlayId(null);
    // Seek the playhead onto the segment so it's actually visible on the canvas — a
    // segment only renders while currentTime is within its [time, time+duration] window.
    // Mirrors onSelectTextOverlay. Skip if the playhead is already inside the segment so
    // we don't yank a user who clicked the segment that's already showing.
    const seg = timelineSegments.find((s) => s.media_id === segmentId);
    if (seg && videoRef.current && (currentTime < seg.time || currentTime >= seg.time + seg.duration)) {
      videoRef.current.currentTime = seg.time;
      setCurrentTime(seg.time);
    }
    // Seed a transform entry so the move/resize box + handles render immediately — even for
    // a default full-frame image. Without this there's no draggable surface to grab.
    setSegmentTransforms((prev) =>
      prev[segmentId] ? prev : { ...prev, [segmentId]: { x: 0, y: 0, width: 100, height: 100 } }
    );
  }, [timelineSegments, currentTime]);

  const sortedSegments = [...timelineSegments].sort((a, b) => a.time - b.time);
  const totalVideoDuration = sortedSegments.length > 0 ? Math.max(...sortedSegments.map(s => s.time + s.duration)) : 0;
  const fmtMMSS = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const getSceneName = (idx: number, total: number) => {
    if (idx === 0) return 'Opening';
    if (idx === total - 1 && total > 1) return 'Call to Action';
    return 'Key Message';
  };

  const openMedia = (tab: typeof mediaModalTab = 'library') => { setMediaModalTab(tab); setShowMediaModal(true); };

  return (
    <div ref={editorRootRef} className="flex h-full flex-col bg-white overflow-hidden" onClick={() => { setSelectedOverlayId(null); setSelectedSegmentId(null); }}>

      {/* ── HEADER (shown only when not embedded in the modal) ── */}
      {!hideHeader && (
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 bg-white px-5 h-12" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-2.5">
            <h1 className="text-sm font-semibold text-gray-900">Video Creator</h1>
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-500">Step 4 of 5</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onApplyEdits} disabled={isApplyingEdits} className="flex items-center gap-1.5 rounded-lg bg-brand-primary px-4 py-1.5 text-[12px] font-semibold text-white transition-colors disabled:opacity-50">
              {isApplyingEdits ? (<><svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Finalizing…</>) : 'Finalize & Continue'}
            </button>
          </div>
        </div>
      )}

      {/* ── MAIN BODY ── */}
      <div className="flex flex-1 min-h-0">

        {/* ── SCRIPT PANEL (260px) ── */}
        <div className="flex flex-shrink-0 w-[260px] flex-col border-r border-gray-100 bg-white overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-100">
            <svg className="h-4 w-4 text-[#2f4941]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            <h3 className="text-[13px] font-semibold text-gray-900">Script</h3>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {scriptContent ? (
              <p className="text-[11px] text-gray-600 leading-relaxed whitespace-pre-wrap">{scriptContent}</p>
            ) : isAnalyzingCaptions ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-2">
                <svg className="animate-spin h-4 w-4 text-[#2f4941]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <p className="text-[11px] text-gray-500">Transcribing audio…</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <p className="text-[11px] text-gray-400">No script provided</p>
              </div>
            )}
          </div>
        </div>

        {/* ── CENTER: Canvas + Controls + Action Toolbar ── */}
        <div className="flex flex-1 flex-col min-h-0 min-w-0">

          {/* Canvas */}
          <div
            ref={canvasViewportRef}
            className={`relative flex flex-1 min-h-0 items-center justify-center overflow-hidden select-none ${canvasZoom > 1 ? 'cursor-grab' : ''}`}
            style={{ background: 'linear-gradient(160deg, var(--color-editor-canvas-from) 0%, var(--color-editor-canvas-to) 100%)' }}
            onPointerDown={handleCanvasPanPointerDown}
            onClick={() => {
              // A pan gesture ends with a click on the background — don't treat it as deselect.
              if (didCanvasPanRef.current) { didCanvasPanRef.current = false; return; }
              setSelectedSegmentId(null); setSelectedOverlayId(null);
            }}
            onDoubleClick={() => { const v = videoRef.current; if (!v) return; if (v.paused || v.ended) { if (v.ended) v.currentTime = 0; v.play().catch(() => {}); } else v.pause(); }}
          >
            <div
              ref={videoContainerRef}
              className="relative overflow-hidden rounded-[2rem] bg-[#1a1a1a]"
              style={{
                aspectRatio: videoFormat === 'landscape' ? '16/9' : videoFormat === 'square' ? '1/1' : '9/16',
                alignSelf: 'stretch', maxWidth: '100%',
                border: '3px solid rgba(255,255,255,0.85)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.15)',
                ...(canvasZoom !== 1 || canvasPan.x !== 0 || canvasPan.y !== 0
                  ? { transform: `translate(${canvasPan.x}px, ${canvasPan.y}px) scale(${canvasZoom})` }
                  : {}),
              }}
            >
              {generatedVideoUrl ? (
                <>
                  {/* Background music audio element (canvas overlay) */}
                  <audio ref={musicAudioRef} loop style={{ display: 'none' }} />
                  <video
                    ref={videoRef}
                    src={generatedVideoUrl}
                    playsInline
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      // Center anchor + center-origin transform mirrors the Creatomate
                      // Base-Video (scale → size, offset_y → y). Same geometry as the
                      // creation modal's avatarPreviewMediaStyle so all previews agree.
                      objectPosition: 'center',
                      transformOrigin: 'center center',
                      transform:
                        avatarZoom !== 1.0 || avatarOffsetY !== 0
                          ? `translateY(${avatarOffsetY * 100}%) scale(${avatarZoom})`
                          : undefined,
                      ...(avatarStyle === 'circle' && !isTalkingPhotoAvatar
                        ? { clipPath: 'circle(50% at 50% 50%)' }
                        : {}),
                      ...(isTalkingPhotoAvatar && avatarMatting
                        ? {
                            backgroundImage:
                              'linear-gradient(45deg, #2a2a2a 25%, transparent 25%), linear-gradient(-45deg, #2a2a2a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #2a2a2a 75%), linear-gradient(-45deg, transparent 75%, #2a2a2a 75%)',
                            backgroundSize: '16px 16px',
                            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
                          }
                        : {}),
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
                    onEnded={() => {
                      setIsPlaying(false);
                      setCurrentTime(0);
                      if (videoRef.current) videoRef.current.currentTime = 0;
                      if (musicAudioRef.current) {
                        musicAudioRef.current.pause();
                        musicAudioRef.current.currentTime = 0;
                      }
                    }}
                  />
                  {timelineSegments.map((seg) => {
                    const isActive = currentTime >= seg.time && currentTime < seg.time + seg.duration;
                    const preview = segPreview?.mediaId === seg.media_id ? segPreview : null;
                    const isSelected = selectedSegmentId === seg.media_id;
                    const isVisible = isActive || preview !== null;
                    const isVideoSeg = seg.segment_type === 'video';
                    const rawVideoUrl = isVideoSeg ? (segmentVideoUrls?.[seg.media_id] ?? '') : '';
                    const videoUrl = /\.(mp4|webm|mov|m4v|avi|mkv)(\?|#|$)/i.test(rawVideoUrl) ? rawVideoUrl : '';
                    const fitClass = seg.fit === 'cover' ? 'object-cover' : 'object-contain';
                    // Preview uses CSS transitions; playback uses per-frame computed styles
                    const animStyle = preview
                      ? getSegAnimPreviewStyle(preview)
                      : getSegmentPlaybackStyle(seg, currentTime);
                    const segOpacity = (seg.opacity ?? 100) / 100;
                    const animOpacity = animStyle.opacity as number | undefined;
                    const finalOpacity = animOpacity !== undefined
                      ? animOpacity * segOpacity
                      : segOpacity < 1 ? segOpacity : undefined;
                    const transform = segmentTransforms[seg.media_id];
                    const hasTransform = transform && (transform.x !== 0 || transform.y !== 0 || transform.width !== 100 || transform.height !== 100);
                    // Self-contained interactive wrap — faithful mirror of the text-overlay
                    // structure: the selection chrome (border + handles) lives INSIDE this same
                    // div and only renders when selected. The selected segment rises to z-2 so
                    // it sits just above sibling clips (handles grabbable over a neighbour) while
                    // staying below the text-overlay layer.
                    const posStyle: React.CSSProperties = hasTransform
                      ? { position: 'absolute', left: `${transform.x}%`, top: `${transform.y}%`, width: `${transform.width}%`, height: `${transform.height}%` }
                      : { position: 'absolute', inset: 0 };
                    const wrapStyle: React.CSSProperties = {
                      ...posStyle,
                      display: isVisible ? 'block' : 'none',
                      zIndex: isSelected ? 2 : 1,
                      pointerEvents: 'auto',
                      ...animStyle,
                      ...(finalOpacity !== undefined ? { opacity: finalOpacity } : {}),
                    };
                    const wrapClass = `select-none touch-none ${isSelected ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`;
                    // pointer-down selects + begins a drag; click is a redundant safety net.
                    const wrapInteractionProps = {
                      onClick: (e: React.MouseEvent) => { e.stopPropagation(); handleSelectSegment(seg.media_id); },
                      onPointerDown: (e: React.PointerEvent) => handleSegmentDragStart(e, seg.media_id),
                      onPointerMove: (e: React.PointerEvent) => handleSegmentDragMove(e, seg.media_id),
                      onPointerUp: (e: React.PointerEvent) => handleSegmentDragEnd(e, seg.media_id),
                    };
                    // Inner clip keeps the media bounded; the wrap itself is NOT clipped so the
                    // dashed frame + handles can extend just outside the media (like text does).
                    const innerClipStyle: React.CSSProperties = { position: 'absolute', inset: 0, overflow: 'hidden' };
                    const cornerHandles: Array<['nw' | 'ne' | 'sw' | 'se', string]> = [
                      ['nw', 'top-0 left-0 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize'],
                      ['ne', 'top-0 right-0 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize'],
                      ['sw', 'bottom-0 left-0 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize'],
                      ['se', 'bottom-0 right-0 translate-x-1/2 translate-y-1/2 cursor-nwse-resize'],
                    ];
                    const edgeHandles: Array<['n' | 's' | 'e' | 'w', string]> = [
                      ['n', 'top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize'],
                      ['s', 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 cursor-ns-resize'],
                      ['e', 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2 cursor-ew-resize'],
                      ['w', 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize'],
                    ];
                    const selectionChrome = isSelected ? (
                      <>
                        <div className="pointer-events-none absolute -inset-1 rounded border-2 border-dashed border-[#2f4941]" />
                        {cornerHandles.map(([handle, className]) => (
                          <div
                            key={handle}
                            className={`absolute h-3.5 w-3.5 rounded-full bg-white border-2 border-[#2f4941] shadow pointer-events-auto ${className}`}
                            onPointerDown={(e) => { e.stopPropagation(); handleSegmentResizeStart(e, seg.media_id, handle); }}
                            onPointerMove={handleSegmentResizeMove}
                            onPointerUp={handleSegmentResizeEnd}
                          />
                        ))}
                        {edgeHandles.map(([handle, className]) => (
                          <div
                            key={handle}
                            className={`absolute h-3 w-3 rounded-sm bg-white border-2 border-[#2f4941] shadow pointer-events-auto ${className}`}
                            onPointerDown={(e) => { e.stopPropagation(); handleSegmentResizeStart(e, seg.media_id, handle); }}
                            onPointerMove={handleSegmentResizeMove}
                            onPointerUp={handleSegmentResizeEnd}
                          />
                        ))}
                      </>
                    ) : null;
                    if (isVideoSeg && videoUrl) {
                      return (
                        <div key={seg.media_id} style={wrapStyle} className={wrapClass} {...wrapInteractionProps}>
                          <div style={innerClipStyle}>
                            <video
                              src={videoUrl}
                              muted
                              loop
                              playsInline
                              className={`absolute inset-0 h-full w-full ${fitClass}`}
                              style={{ pointerEvents: 'none' }}
                              ref={(el) => {
                                if (!el) return;
                                const trimStart = seg.trim_start ?? 0;
                                if (isActive) {
                                  const relativeTime = currentTime - seg.time + trimStart;
                                  const timeDiff = Math.abs(el.currentTime - relativeTime);
                                  if (timeDiff > 0.1) el.currentTime = relativeTime;
                                  if (isPlaying) {
                                    if (el.paused) el.play().catch(() => {});
                                  } else {
                                    if (!el.paused) el.pause();
                                  }
                                } else {
                                  if (isSelected && Math.abs(el.currentTime - trimStart) > 0.1) {
                                    try { el.currentTime = trimStart; } catch {}
                                  }
                                  if (!el.paused) el.pause();
                                }
                              }}
                            />
                          </div>
                          {selectionChrome}
                        </div>
                      );
                    }
                    const thumbUrl = thumbnailById[seg.media_id];
                    if (!thumbUrl) return null;
                    return (
                      <div key={seg.media_id} style={wrapStyle} className={wrapClass} {...wrapInteractionProps}>
                        <div style={innerClipStyle}>
                          <img src={thumbUrl} alt="" className={`absolute inset-0 h-full w-full ${fitClass}`} style={{ pointerEvents: 'none' }} draggable={false} />
                        </div>
                        {selectionChrome}
                      </div>
                    );
                  })}
                  {selectedSegmentId && segmentTransforms[selectedSegmentId] && selectedSegment &&
                    currentTime >= selectedSegment.time && currentTime < selectedSegment.time + selectedSegment.duration && (
                    <div className="absolute inset-0 z-20" style={{ pointerEvents: 'none' }}>
                      {(() => {
                        const transform = segmentTransforms[selectedSegmentId];
                        if (!transform) return null;
                        const cornerHandles: Array<['nw' | 'ne' | 'sw' | 'se', string]> = [
                          ['nw', 'top-0 left-0 -translate-x-2 -translate-y-2'],
                          ['ne', 'top-0 right-0 translate-x-2 -translate-y-2'],
                          ['sw', 'bottom-0 left-0 -translate-x-2 translate-y-2'],
                          ['se', 'bottom-0 right-0 translate-x-2 translate-y-2'],
                        ];
                        const edgeHandles: Array<['n' | 's' | 'e' | 'w', string, string]> = [
                          ['n', 'top-0 left-1/2 -translate-x-1/2 -translate-y-2', 'cursor-ns-resize'],
                          ['s', 'bottom-0 left-1/2 -translate-x-1/2 translate-y-2', 'cursor-ns-resize'],
                          ['e', 'right-0 top-1/2 -translate-y-1/2 translate-x-2', 'cursor-ew-resize'],
                          ['w', 'left-0 top-1/2 -translate-y-1/2 -translate-x-2', 'cursor-ew-resize'],
                        ];
                        return (
                          <div
                            className="absolute cursor-move ring-2 ring-[#2f4941] ring-inset"
                            style={{
                              left: `${transform.x}%`,
                              top: `${transform.y}%`,
                              width: `${transform.width}%`,
                              height: `${transform.height}%`,
                              pointerEvents: 'auto',
                              touchAction: 'none',
                            }}
                            onPointerMove={(e) => handleSegmentDragMove(e as any, selectedSegmentId)}
                            onPointerUp={(e) => handleSegmentDragEnd(e as any, selectedSegmentId)}
                            onPointerDown={(e) => handleSegmentDragStart(e as any, selectedSegmentId)}
                          >
                            {/* Corner handles */}
                            {cornerHandles.map(([handle, className]) => (
                              <div
                                key={handle}
                                className={`absolute w-4 h-4 bg-[#2f4941] rounded-full border-2 border-white shadow-lg ${className} pointer-events-auto`}
                                onPointerDown={(e) => { e.stopPropagation(); handleSegmentResizeStart(e as any, selectedSegmentId, handle); }}
                                onPointerMove={handleSegmentResizeMove}
                                onPointerUp={handleSegmentResizeEnd}
                                style={{ cursor: handle === 'nw' || handle === 'se' ? 'nwse-resize' : 'nesw-resize' }}
                              />
                            ))}
                            {/* Edge handles */}
                            {edgeHandles.map(([handle, className, cursorClass]) => (
                              <div
                                key={handle}
                                className={`absolute w-3 h-3 bg-[#2f4941] rounded border border-white shadow ${className} ${cursorClass} pointer-events-auto`}
                                onPointerDown={(e) => { e.stopPropagation(); handleSegmentResizeStart(e as any, selectedSegmentId, handle); }}
                                onPointerMove={handleSegmentResizeMove}
                                onPointerUp={handleSegmentResizeEnd}
                              />
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div className="absolute inset-0 z-10" style={{ pointerEvents: 'none' }}>
                    {textOverlays.map((overlay) => {
                      const inRange = currentTime >= overlay.time && currentTime < overlay.time + overlay.duration;
                      if (!inRange) return null;
                      const pos = getOverlayPos(overlay);
                      const isSelected = selectedOverlayId === overlay.id;
                      const isEditing = editingOverlayId === overlay.id;
                      // Layout (unscaled) dims — getBoundingClientRect() would include the canvas
                      // zoom transform and double-scale the font (CSS transform scales it again).
                      const containerEl = videoContainerRef.current;
                      const containerMin = containerEl ? Math.min(containerEl.offsetWidth, containerEl.offsetHeight) : 360;
                      const fontVmin = overlay.fontSize ?? (overlay.style === 'title' ? 8 : overlay.style === 'subtitle' ? 5.5 : 4.5);
                      const fontSize = Math.max(10, (fontVmin / 100) * containerMin);
                      const designKey = overlay.design ?? 'classic';
                      const { className: overlayClass, style: overlayStyle } = getCaptionOverlayStyles(designKey, isSelected && !isEditing);
                      const textColor = overlay.color ?? (overlayStyle as any).color ?? '#ffffff';
                      // Outline: explicit overlay.outlineColor wins; otherwise pick a contrast
                      // stroke (white text → black stroke, dark text → white). Mirrors the BE.
                      const autoOutlineColor = (textColor === '#000000' || textColor === '#2f4941') ? '#ffffff' : '#000000';
                      const outlineColor = overlay.outlineColor ?? autoOutlineColor;
                      // Skip animation when paused so the overlay is always visible when seeking/clicking
                      const animStyle = (isEditing || !isPlaying) ? {} as React.CSSProperties : getOverlayAnimStyle(overlay, currentTime);
                      const { transform: animTransform, ...animRestStyle } = animStyle;
                      const rotation = overlay.rotation ?? 0;
                      const baseTransform = `translate(-50%, -50%) rotate(${rotation}deg)`;
                      const fullTransform = isEditing ? baseTransform : `${baseTransform} ${animTransform ?? ''}`.trim();
                      const overlayWidthPct = overlay.width ?? 56;
                      const bgColor = overlay.backgroundColor;
                      const previewFontFamily = `"${overlay.fontFamily ?? 'Open Sans'}", "Open Sans", system-ui, sans-serif`;
                      const textStyle: React.CSSProperties = {
                        fontSize,
                        width: '100%',
                        ...overlayStyle,
                        color: textColor,
                        fontFamily: previewFontFamily,
                        fontWeight: overlay.bold ? 'bold' : (overlayStyle as any).fontWeight,
                        fontStyle: overlay.italic ? 'italic' : undefined,
                        // Render multi-line overlays (e.g. title + subtitle templates) with line breaks.
                        whiteSpace: 'pre-line',
                        lineHeight: 1.15,
                        ...(bgColor ? {
                          backgroundColor: bgColor,
                          borderRadius: overlay.design === 'pill' ? '999px' : '6px',
                          padding: overlay.design === 'pill' ? '6px 20px' : '4px 12px',
                        } : {}),
                        // Explicit outline wins over any design-preset stroke; paintOrder keeps the fill crisp.
                        ...(overlay.outline ? {
                          WebkitTextStroke: `${Math.max(1, fontSize * 0.06)}px ${outlineColor}`,
                          paintOrder: 'stroke fill',
                        } : {}),
                      };
                      return (
                        <div
                          key={overlay.id}
                          className={`absolute select-none touch-none ${isEditing ? 'cursor-text z-40' : `cursor-grab active:cursor-grabbing ${isSelected ? 'z-30' : 'z-20'}`}`}
                          style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: fullTransform, width: `${overlayWidthPct}%`, pointerEvents: 'auto', transition: isSelected ? 'none' : undefined, ...animRestStyle }}
                          onClick={e => { e.stopPropagation(); if (!isEditing) { setSelectedOverlayId(overlay.id); setSelectedSegmentId(null); } }}
                          onDoubleClick={e => { e.stopPropagation(); setSelectedOverlayId(overlay.id); setSelectedSegmentId(null); setEditingOverlayId(overlay.id); }}
                          onPointerDown={e => { if (!isEditing) handleOverlayPointerDown(e, overlay.id); }}
                          onPointerMove={e => { if (!isEditing) handleOverlayPointerMove(e, overlay.id); }}
                          onPointerUp={e => { if (!isEditing) handleOverlayPointerUp(e, overlay.id); }}
                        >
                          {isEditing ? (
                            <textarea
                              autoFocus
                              value={overlay.text}
                              placeholder="Type your text…"
                              onFocus={e => e.currentTarget.select()}
                              onChange={e => onUpdateTextOverlay?.(overlay.id, { text: e.target.value })}
                              onBlur={() => setEditingOverlayId(null)}
                              onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setEditingOverlayId(null); } }}
                              onPointerDown={e => e.stopPropagation()}
                              onClick={e => e.stopPropagation()}
                              className="resize-none rounded text-center font-bold outline-none placeholder:text-white/40 w-full"
                              style={{ fontSize, minWidth: '80px', padding: '4px 12px', border: '2px solid #2f4941', color: textColor, background: 'rgba(0,0,0,0.85)', fontFamily: previewFontFamily, fontWeight: overlay.bold ? 'bold' : undefined, fontStyle: overlay.italic ? 'italic' : undefined }}
                              rows={2}
                            />
                          ) : (
                            <div className="relative">
                              <div className={overlayClass} style={textStyle}>{overlay.text || 'Double-click to edit'}</div>
                              {isSelected && (
                                <>
                                  {/* Selection frame */}
                                  <div className="pointer-events-none absolute -inset-2 rounded border-2 border-dashed border-[#2f4941]/90" />
                                  {/* Rotation handle — top center */}
                                  <div className="absolute left-1/2 pointer-events-none" style={{ bottom: 'calc(100% + 8px)', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                    <div
                                      className="h-6 w-6 rounded-full bg-white border-2 border-[#2f4941] shadow-md cursor-grab active:cursor-grabbing flex items-center justify-center pointer-events-auto hover:bg-[#f0f4f2] transition-colors"
                                      title="Drag to rotate"
                                      onPointerDown={e => handleOverlayRotateDown(e, overlay)}
                                      onPointerMove={handleOverlayRotateMove}
                                      onPointerUp={handleOverlayRotateUp}
                                      onPointerCancel={handleOverlayRotateUp}
                                    >
                                      <svg className="h-3.5 w-3.5 text-[#2f4941]" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M4.07 15a8 8 0 1 0 .85-5.5" /></svg>
                                    </div>
                                    <div className="w-px bg-[#2f4941]/60" style={{ height: 8 }} />
                                  </div>
                                  {/* Corner resize handles — horizontal changes width, vertical changes fontSize */}
                                  {([
                                    ['top','left','nwse-resize',-1,-1],
                                    ['top','right','nesw-resize',1,-1],
                                    ['bottom','left','nesw-resize',-1,1],
                                    ['bottom','right','nwse-resize',1,1],
                                  ] as const).map(([v, h, cur, hd, vd]) => (
                                    <div
                                      key={`${v}-${h}`}
                                      className="absolute h-3.5 w-3.5 rounded-full bg-white border-2 border-[#2f4941] shadow pointer-events-auto"
                                      style={{ [v]: -6, [h]: -6, cursor: cur }}
                                      onPointerDown={e => handleOverlayResizeDown(e, overlay, 'both', hd, vd)}
                                      onPointerMove={handleOverlayResizeMove}
                                      onPointerUp={handleOverlayResizeUp}
                                      onPointerCancel={handleOverlayResizeUp}
                                    />
                                  ))}
                                  {/* Left/right side handles — change text box width */}
                                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 flex h-5 w-2.5 cursor-ew-resize items-center justify-center gap-0.5 rounded-sm bg-[#2f4941] shadow pointer-events-auto" onPointerDown={e => handleOverlayResizeDown(e, overlay, 'width', -1)} onPointerMove={handleOverlayResizeMove} onPointerUp={handleOverlayResizeUp} onPointerCancel={handleOverlayResizeUp}><div className="h-2.5 w-px bg-white" /><div className="h-2.5 w-px bg-white" /></div>
                                  <div className="absolute -right-3 top-1/2 -translate-y-1/2 flex h-5 w-2.5 cursor-ew-resize items-center justify-center gap-0.5 rounded-sm bg-[#2f4941] shadow pointer-events-auto" onPointerDown={e => handleOverlayResizeDown(e, overlay, 'width', 1)} onPointerMove={handleOverlayResizeMove} onPointerUp={handleOverlayResizeUp} onPointerCancel={handleOverlayResizeUp}><div className="h-2.5 w-px bg-white" /><div className="h-2.5 w-px bg-white" /></div>
                                  {/* Top/bottom side handles — change font size */}
                                  <div className="absolute left-1/2 -translate-x-1/2 -top-3 flex w-5 h-2.5 cursor-ns-resize items-center justify-center gap-0.5 rounded-sm bg-[#2f4941] shadow pointer-events-auto" onPointerDown={e => handleOverlayResizeDown(e, overlay, 'font', 1, -1)} onPointerMove={handleOverlayResizeMove} onPointerUp={handleOverlayResizeUp} onPointerCancel={handleOverlayResizeUp}><div className="w-2.5 h-px bg-white" /><div className="w-2.5 h-px bg-white" /></div>
                                  <div className="absolute left-1/2 -translate-x-1/2 -bottom-3 flex w-5 h-2.5 cursor-ns-resize items-center justify-center gap-0.5 rounded-sm bg-[#2f4941] shadow pointer-events-auto" onPointerDown={e => handleOverlayResizeDown(e, overlay, 'font', 1, 1)} onPointerMove={handleOverlayResizeMove} onPointerUp={handleOverlayResizeUp} onPointerCancel={handleOverlayResizeUp}><div className="w-2.5 h-px bg-white" /><div className="w-2.5 h-px bg-white" /></div>
                                  {/* Rotation degree badge */}
                                  {rotation !== 0 && <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-mono text-white pointer-events-none">{rotation}°</div>}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                  <div className="mb-3 text-5xl">🎬</div>
                  <p className="text-sm text-gray-400">No video loaded</p>
                </div>
              )}
            </div>

            {/* Canvas zoom controls — Ctrl/Cmd+scroll also zooms; drag the background to pan when zoomed. */}
            <div
              className="absolute bottom-3 right-3 z-40 flex items-center gap-1 rounded-full border border-white/70 bg-white/90 px-2 py-1 shadow-md backdrop-blur-sm"
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              title="Canvas zoom (Ctrl+scroll)"
            >
              <button
                type="button"
                onClick={() => setCanvasZoomClamped(canvasZoom - 0.25)}
                disabled={canvasZoom <= 0.5}
                aria-label="Zoom canvas out"
                className="flex h-5 w-5 items-center justify-center rounded-full text-[13px] font-bold leading-none text-gray-600 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-40"
              >−</button>
              <button
                type="button"
                onClick={() => setCanvasZoomClamped(1)}
                className="w-9 text-center font-mono text-[10px] text-gray-600 hover:text-gray-900"
                title="Reset zoom"
              >
                {Math.round(canvasZoom * 100)}%
              </button>
              <button
                type="button"
                onClick={() => setCanvasZoomClamped(canvasZoom + 0.25)}
                disabled={canvasZoom >= 2}
                aria-label="Zoom canvas in"
                className="flex h-5 w-5 items-center justify-center rounded-full text-[13px] font-bold leading-none text-gray-600 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-40"
              >+</button>
            </div>
          </div>

          {/* Playback controls */}
          <div className="flex flex-shrink-0 items-center gap-3 border-t border-gray-100 bg-white px-4 h-9" onClick={e => e.stopPropagation()}>
            <button data-tour="play" onClick={() => { const v = videoRef.current; if (!v) return; if (v.paused || v.ended) { if (v.ended) v.currentTime = 0; v.play().catch(() => {}); } else { v.pause(); } }} className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-700 transition-colors">
              {isPlaying ? <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg> : <svg className="ml-0.5 h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>}
            </button>
            <span className="flex-shrink-0 font-mono text-[11px] text-gray-500">{fmtTime(currentTime)} / {fmtTime(duration)}</span>
            <input type="range" min={0} max={duration} step={0.01} value={currentTime} onChange={e => { const t = parseFloat(e.target.value); setCurrentTime(t); if (videoRef.current) videoRef.current.currentTime = t; }} className="flex-1 cursor-pointer" style={{ accentColor: '#2f4941' }} />
            <div className="flex flex-shrink-0 items-center gap-1 ml-1 border-l border-gray-100 pl-3">
              <span className="text-[10px] font-medium text-gray-400 mr-0.5">Zoom</span>
              <button onClick={() => onAvatarZoomChange?.(Math.max(0.5, parseFloat((avatarZoom - 0.05).toFixed(2))))} className="flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors text-sm font-bold">−</button>
              <span className="w-9 text-center font-mono text-[11px] text-gray-700">{Math.round(avatarZoom * 100)}%</span>
              <button onClick={() => onAvatarZoomChange?.(Math.min(2.0, parseFloat((avatarZoom + 0.05).toFixed(2))))} className="flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors text-sm font-bold">+</button>
            </div>
          </div>

          {/* Music overlay bar — shown when a track is selected */}
          {resolvedMusicUrl && (
            <div className="flex flex-shrink-0 items-center gap-2.5 border-t border-gray-100 bg-white px-4 h-9" onClick={e => e.stopPropagation()}>
              <svg className="h-3.5 w-3.5 flex-shrink-0 text-[#2f4941]" fill="currentColor" viewBox="0 0 24 24"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>
              <span className="flex-1 truncate text-[11px] font-medium text-gray-700">
                {UGC_BACKGROUND_MUSIC_PRESETS.find(p => p.id === musicPresetId)?.title ?? 'Custom track'}
              </span>
              <svg className="h-3 w-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              <span className="flex-shrink-0 text-[10px] text-gray-400 w-6 text-right">{musicVolumePercent}%</span>
              <input
                type="range" min={0} max={100} value={musicVolumePercent}
                onChange={e => {
                  const v = Number(e.target.value);
                  onMusicVolumeChange?.(v);
                  if (musicAudioRef.current) musicAudioRef.current.volume = Math.min(1, Math.max(0, v / 100));
                }}
                className="w-20 cursor-pointer"
                style={{ accentColor: '#2f4941' }}
              />
            </div>
          )}

          {/* Action Toolbar */}
          <div className="flex flex-shrink-0 items-center justify-center border-t border-gray-100 bg-white" onClick={e => e.stopPropagation()}>
            {([
              { label: 'Text', onClick: handleAddTextOverlay, icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 7V4h16v3M9 20h6M12 4v16" /></svg> },
              { label: 'Templates', onClick: () => openMedia('templates'), icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="7" rx="1.5" /><rect x="3" y="14" width="8" height="7" rx="1.5" /><rect x="15" y="14" width="6" height="7" rx="1.5" /></svg> },
              { label: 'Media', onClick: () => openMedia('library'), icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" strokeLinecap="round" strokeLinejoin="round" /></svg> },
              { label: 'Music', onClick: () => openMedia('music'), icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path d="M9 18V5l12-2v13" strokeLinecap="round" strokeLinejoin="round" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg> },
              { label: 'Captions', onClick: () => onCaptionsEnabledChange?.(!captionsEnabled), icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><rect x="2" y="6" width="20" height="12" rx="2" /><path strokeLinecap="round" d="M6 12h4M6 15h8M14 12h4" /></svg> },
              { label: 'Delete', danger: true, onClick: () => { if (selectedSegmentId) { onRemoveSegment?.(selectedSegmentId); setSelectedSegmentId(null); } else if (selectedOverlayId) { removeOverlayOrGroup(selectedOverlayId); setSelectedOverlayId(null); } }, icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg> },
              { label: 'Help', onClick: () => setShowHelpTour(true), icon: <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" strokeLinejoin="round" d="M9.5 9a2.5 2.5 0 0 1 4.5 1.5c0 1.5-2 2-2 3M12 17h.01" /></svg> },
            ] as const).map((action: any) => (
              <button key={action.label} data-tour={action.label.toLowerCase()} onClick={action.onClick} className={`flex flex-col items-center gap-0.5 px-3.5 py-1.5 transition-colors ${action.danger ? 'text-red-400 hover:bg-red-50' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}>
                {action.icon}
                <span className="text-[9px] font-medium">{action.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── RIGHT PANEL (256px) ── */}
        <div className="flex w-64 flex-shrink-0 flex-col border-l border-gray-100 bg-white overflow-y-auto" onClick={e => e.stopPropagation()}>

          {/* Selected segment properties — pinned at top */}
          {selectedSegment && (
            <div className="flex-shrink-0 border-b-2 border-[#2f4941]/40 bg-gradient-to-b from-[#f0f4f2] to-white px-4 py-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-[#2f4941] text-white">
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 15l-5-5L5 21" /></svg>
                  </span>
                  <h4 className="text-[11px] font-bold text-gray-900">Clip {timelineSegments.findIndex(s => s.media_id === selectedSegment.media_id) + 1}</h4>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { onRemoveSegment?.(selectedSegment.media_id); setSelectedSegmentId(null); }} className="text-[10px] font-medium text-red-500 hover:text-red-600">Remove</button>
                  <button onClick={() => setSelectedSegmentId(null)} className="text-gray-400 hover:text-gray-600">
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>

              {/* Timing */}
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[10px] text-gray-400 block mb-1">Start (s)</label><input type="number" value={selectedSegment.time.toFixed(1)} step={0.1} onChange={e => onUpdateSegment?.(selectedSegment.media_id, { time: parseFloat(e.target.value) })} className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[11px] outline-none focus:border-[#2f4941]" /></div>
                <div><label className="text-[10px] text-gray-400 block mb-1">Duration (s)</label><input type="number" value={selectedSegment.duration.toFixed(1)} step={0.1} onChange={e => onUpdateSegment?.(selectedSegment.media_id, { duration: parseFloat(e.target.value) })} className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[11px] outline-none focus:border-[#2f4941]" /></div>
              </div>

              {/* Fit */}
              <div className="flex gap-2">
                <button onClick={() => onUpdateSegment?.(selectedSegment.media_id, { fit: 'cover' })} className={`flex-1 rounded-lg py-1.5 text-[11px] font-medium transition-colors ${selectedSegment.fit !== 'contain' ? 'bg-[#2f4941] text-white' : 'bg-theme-background text-theme-fg-muted hover:bg-surface-ui-hover'}`}>Cover</button>
                <button onClick={() => onUpdateSegment?.(selectedSegment.media_id, { fit: 'contain' })} className={`flex-1 rounded-lg py-1.5 text-[11px] font-medium transition-colors ${selectedSegment.fit === 'contain' ? 'bg-[#2f4941] text-white' : 'bg-theme-background text-theme-fg-muted hover:bg-surface-ui-hover'}`}>Contain</button>
              </div>

              {/* Opacity */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Opacity</p>
                  <span className="font-mono text-[10px] text-gray-500">{selectedSegment.opacity ?? 100}%</span>
                </div>
                <input
                  type="range" min={0} max={100} step={1}
                  value={selectedSegment.opacity ?? 100}
                  onChange={e => onUpdateSegment?.(selectedSegment.media_id, { opacity: parseInt(e.target.value) })}
                  className="w-full accent-[#2f4941]"
                />
              </div>

              {/* Entry animation */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Entry</p>
                  {(selectedSegment.animation_in ?? 'none') !== 'none' && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] text-gray-400">Dur</span>
                      <input type="range" min={0.1} max={2} step={0.1} value={selectedSegment.anim_in_duration ?? 0.5} onChange={e => onUpdateSegment?.(selectedSegment.media_id, { anim_in_duration: parseFloat(e.target.value) })} className="w-14 accent-[#2f4941]" />
                      <span className="w-6 text-right font-mono text-[9px] text-gray-500">{(selectedSegment.anim_in_duration ?? 0.5).toFixed(1)}s</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {SEGMENT_ANIM_IN_OPTIONS.map(opt => {
                    const active = (selectedSegment.animation_in ?? 'none') === opt.value;
                    return (
                      <button key={opt.value} title={opt.title}
                        onClick={() => { onUpdateSegment?.(selectedSegment.media_id, { animation_in: opt.value }); triggerAnimPreview(selectedSegment, opt.value, 'in'); }}
                        className={`rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors ${active ? 'bg-[#2f4941] text-white' : 'bg-theme-background text-theme-fg-muted hover:bg-surface-ui-hover'}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Motion (images only) */}
              {selectedSegment.segment_type !== 'video' && (
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Motion</p>
                  <div className="flex flex-wrap gap-1">
                    {SEGMENT_MOTION_OPTIONS.map(opt => {
                      const active = (selectedSegment.motion ?? 'none') === opt.value;
                      return (
                        <button key={opt.value} title={opt.title}
                          onClick={() => onUpdateSegment?.(selectedSegment.media_id, { motion: opt.value })}
                          className={`rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors ${active ? 'bg-[#2f4941] text-white' : 'bg-theme-background text-theme-fg-muted hover:bg-surface-ui-hover'}`}>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Exit animation */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Exit</p>
                  {(selectedSegment.animation_out ?? 'none') !== 'none' && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] text-gray-400">Dur</span>
                      <input type="range" min={0.1} max={2} step={0.1} value={selectedSegment.anim_out_duration ?? 0.5} onChange={e => onUpdateSegment?.(selectedSegment.media_id, { anim_out_duration: parseFloat(e.target.value) })} className="w-14 accent-[#2f4941]" />
                      <span className="w-6 text-right font-mono text-[9px] text-gray-500">{(selectedSegment.anim_out_duration ?? 0.5).toFixed(1)}s</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {SEGMENT_ANIM_OUT_OPTIONS.map(opt => {
                    const active = (selectedSegment.animation_out ?? 'none') === opt.value;
                    return (
                      <button key={opt.value} title={opt.title}
                        onClick={() => { onUpdateSegment?.(selectedSegment.media_id, { animation_out: opt.value }); triggerAnimPreview(selectedSegment, opt.value, 'out'); }}
                        className={`rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors ${active ? 'bg-[#2f4941] text-white' : 'bg-theme-background text-theme-fg-muted hover:bg-surface-ui-hover'}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Selected overlay properties — pinned at top so designs/animations/duration are always visible */}
          {selectedOverlay && (
            <div className="flex-shrink-0 border-b-2 border-[#2f4941]/40 bg-gradient-to-b from-[#f0f4f2] to-white px-4 py-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="flex h-5 w-5 items-center justify-center rounded bg-[#2f4941] text-white">
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 7V4h16v3M9 20h6M12 4v16" /></svg>
                  </span>
                  <h4 className="text-[11px] font-bold text-gray-900">Text Overlay</h4>
                </div>
                <button onClick={() => { removeOverlayOrGroup(selectedOverlay.id); setSelectedOverlayId(null); }} className="text-[10px] font-medium text-red-500 hover:text-red-600">Remove</button>
              </div>
              <textarea value={selectedOverlay.text} onChange={e => onUpdateTextOverlay?.(selectedOverlay.id, { text: e.target.value })} className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-[11px] resize-none outline-none focus:border-[#2f4941]" rows={2} placeholder="Enter text…" />

              <div className="flex gap-1">{(['top', 'center', 'bottom'] as const).map(p => <button key={p} onClick={() => { onUpdateTextOverlay?.(selectedOverlay.id, { position: p }); setOverlayPositions(prev => ({ ...prev, [selectedOverlay.id]: { x: prev[selectedOverlay.id]?.x ?? 50, y: posToY(p) } })); }} className={`flex-1 rounded-lg py-1.5 text-[10px] font-medium capitalize transition-colors ${selectedOverlay.position === p ? 'bg-[#2f4941] text-white' : 'bg-theme-background text-theme-fg-muted hover:bg-surface-ui-hover'}`}>{p}</button>)}</div>

              {/* Style presets — one-tap starting point; manual color/font live in the groups below. */}
              <div>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Style presets</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {TEXT_DESIGN_OPTIONS.map(d => {
                    const active = (selectedOverlay.design ?? 'classic') === d.value;
                    return (
                      <button
                        key={d.value}
                        onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { design: d.value })}
                        title={d.label}
                        className={`relative rounded-lg border px-1 py-2 text-center transition-all overflow-hidden ${active ? 'border-[#2f4941] ring-2 ring-[#2f4941]/40' : 'border-gray-200 hover:border-gray-300'}`}
                        style={{ background: d.preview.bg, minHeight: 32 }}
                      >
                        <span
                          className="block text-[9px] font-black leading-tight"
                          style={{
                            color: d.value === 'gradient' ? 'transparent' : d.preview.color,
                            WebkitTextStroke: d.preview.stroke ? `0.4px ${d.preview.stroke}` : undefined,
                            background: d.value === 'gradient' ? 'linear-gradient(90deg, #ff7a18, #af002d)' : undefined,
                            WebkitBackgroundClip: d.value === 'gradient' ? 'text' : undefined,
                            backgroundClip: d.value === 'gradient' ? ('text' as any) : undefined,
                          }}
                        >
                          {d.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Typography group: font, size, weight ── */}
              <div className="space-y-3 border-t border-gray-100 pt-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Typography</p>

                {/* Font family — curated Google Fonts (mirrors backend allowlist). */}
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Font</p>
                  <select
                    value={selectedOverlay.fontFamily ?? UGC_DEFAULT_TEXT_FONT}
                    onChange={e => {
                      const family = e.target.value;
                      loadGoogleFont(family);
                      onUpdateTextOverlay?.(selectedOverlay.id, { fontFamily: family });
                    }}
                    className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[11px] outline-none focus:border-[#2f4941]"
                  >
                    {UGC_TEXT_FONTS.map(f => (
                      <option key={f.family} value={f.family} style={{ fontFamily: `"${f.family}", system-ui, sans-serif` }}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Font size slider */}
                <div>
                  <div className="mb-1 flex items-baseline justify-between">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Size</p>
                    <span className="font-mono text-[10px] text-gray-400">{(selectedOverlay.fontSize ?? (selectedOverlay.style === 'title' ? 8 : selectedOverlay.style === 'subtitle' ? 5.5 : 4.5)).toFixed(1)}</span>
                  </div>
                  <input
                    type="range"
                    min={3} max={16} step={0.5}
                    value={selectedOverlay.fontSize ?? (selectedOverlay.style === 'title' ? 8 : selectedOverlay.style === 'subtitle' ? 5.5 : 4.5)}
                    onChange={e => onUpdateTextOverlay?.(selectedOverlay.id, { fontSize: parseFloat(e.target.value) })}
                    className="w-full accent-[#2f4941]"
                  />
                </div>

                {/* Bold / italic */}
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Bold / italic</p>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { bold: !selectedOverlay.bold })}
                      className={`flex-1 rounded-lg py-1.5 text-[11px] font-bold transition-colors ${selectedOverlay.bold ? 'bg-[#2f4941] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >B</button>
                    <button
                      onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { italic: !selectedOverlay.italic })}
                      className={`flex-1 rounded-lg py-1.5 text-[11px] italic font-medium transition-colors ${selectedOverlay.italic ? 'bg-[#2f4941] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >I</button>
                  </div>
                </div>
              </div>

              {/* ── Color & outline group: fill, outline (+ color), background ── */}
              <div className="space-y-3 border-t border-gray-100 pt-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Color &amp; outline</p>

                {/* Text color — preset swatches + custom color picker */}
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Color</p>
                    {selectedOverlay.color && (
                      <button onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { color: undefined })} className="text-[9px] text-gray-400 hover:text-gray-600">Auto</button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(['#ffffff','#000000','#FFD700','#FF6B00','#00FF00','#00CFFF','#FF3366','#B44FFF','#2f4941'] as string[]).map(c => (
                      <button
                        key={c}
                        onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { color: c })}
                        className="h-5 w-5 rounded-full border-2 transition-all"
                        style={{ background: c, borderColor: selectedOverlay.color === c ? '#2f4941' : 'transparent', boxShadow: selectedOverlay.color === c ? '0 0 0 2px rgba(47,73,65,0.4)' : '0 0 0 1px rgba(0,0,0,0.15)' }}
                        title={c}
                      />
                    ))}
                    <label className="relative h-5 w-5 rounded-full cursor-pointer overflow-hidden border border-gray-300" title="Custom color" style={{ background: 'conic-gradient(red,yellow,lime,cyan,blue,magenta,red)' }}>
                      <input type="color" className="absolute inset-0 h-full w-full opacity-0 cursor-pointer" value={selectedOverlay.color ?? '#ffffff'} onChange={e => onUpdateTextOverlay?.(selectedOverlay.id, { color: e.target.value })} />
                    </label>
                  </div>
                </div>

                {/* Outline — toggle, then its color swatches when on (kept together to avoid the
                 *  old split where the toggle and its color lived in different sections). */}
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Outline</p>
                    {selectedOverlay.outline && selectedOverlay.outlineColor && (
                      <button onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { outlineColor: undefined })} className="text-[9px] text-gray-400 hover:text-gray-600">Auto</button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { outline: !selectedOverlay.outline })}
                      title="Toggle outline"
                      className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition-colors ${selectedOverlay.outline ? 'bg-[#2f4941] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      <span style={{ WebkitTextStroke: `0.7px ${selectedOverlay.outline ? '#ffffff' : '#4b5563'}`, color: 'transparent' }}>A</span>
                    </button>
                    {selectedOverlay.outline && (
                      <>
                        {(['#000000','#ffffff','#FFD700','#FF6B00','#00FF00','#00CFFF','#FF3366','#B44FFF','#2f4941'] as string[]).map(c => (
                          <button
                            key={c}
                            onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { outlineColor: c })}
                            className="h-5 w-5 rounded-full border-2 transition-all"
                            style={{ background: c, borderColor: selectedOverlay.outlineColor === c ? '#2f4941' : 'transparent', boxShadow: selectedOverlay.outlineColor === c ? '0 0 0 2px rgba(47,73,65,0.4)' : '0 0 0 1px rgba(0,0,0,0.15)' }}
                            title={c}
                          />
                        ))}
                        <label className="relative h-5 w-5 rounded-full cursor-pointer overflow-hidden border border-gray-300" title="Custom outline color" style={{ background: 'conic-gradient(red,yellow,lime,cyan,blue,magenta,red)' }}>
                          <input type="color" className="absolute inset-0 h-full w-full opacity-0 cursor-pointer" value={selectedOverlay.outlineColor ?? '#000000'} onChange={e => onUpdateTextOverlay?.(selectedOverlay.id, { outlineColor: e.target.value })} />
                        </label>
                      </>
                    )}
                  </div>
                </div>

                {/* Background — optional fill behind text */}
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Background</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { backgroundColor: undefined })}
                      className={`rounded-lg border px-2.5 py-1 text-[10px] font-medium transition-all ${!selectedOverlay.backgroundColor ? 'border-[#2f4941] bg-[#f0f4f2] text-[#2f4941]' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    >
                      None
                    </button>
                    {([
                      { label: 'Black',  value: 'rgba(0,0,0,0.85)' },
                      { label: 'Navy',   value: 'rgba(15,23,42,0.92)' },
                      { label: 'White',  value: 'rgba(255,255,255,0.92)' },
                      { label: 'Purple', value: 'rgba(99,102,241,0.92)' },
                      { label: 'Yellow', value: '#FFE100' },
                      { label: 'Red',    value: 'rgba(220,38,38,0.9)' },
                    ] as const).map(({ label, value }) => (
                      <button
                        key={value}
                        onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { backgroundColor: value })}
                        title={label}
                        className="h-5 w-5 rounded-full border-2 transition-all"
                        style={{
                          background: value,
                          borderColor: selectedOverlay.backgroundColor === value ? '#2f4941' : 'transparent',
                          boxShadow: selectedOverlay.backgroundColor === value ? '0 0 0 2px rgba(47,73,65,0.4)' : '0 0 0 1px rgba(0,0,0,0.15)',
                        }}
                      />
                    ))}
                    <label
                      className="relative h-5 w-5 cursor-pointer overflow-hidden rounded-full border border-gray-300"
                      title="Custom background color"
                      style={{ background: 'conic-gradient(red,yellow,lime,cyan,blue,magenta,red)' }}
                    >
                      <input
                        type="color"
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        value={selectedOverlay.backgroundColor && !selectedOverlay.backgroundColor.startsWith('rgba') ? selectedOverlay.backgroundColor : '#000000'}
                        onChange={e => onUpdateTextOverlay?.(selectedOverlay.id, { backgroundColor: e.target.value })}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* ── Animation & transform group ── */}
              <div className="space-y-3 border-t border-gray-100 pt-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Animation &amp; transform</p>

                {/* Animation — IN + OUT */}
                <div className="space-y-2.5">

                {/* IN */}
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-2.5 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#2f4941]">In</span>
                    {(selectedOverlay.animation ?? 'none') !== 'none' && (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="range" min={0.1} max={2} step={0.05}
                          value={selectedOverlay.anim_duration ?? _overlayDefaultInDur(selectedOverlay.animation ?? 'none')}
                          onChange={e => onUpdateTextOverlay?.(selectedOverlay.id, { anim_duration: parseFloat(e.target.value) })}
                          className="w-16 accent-[#2f4941]"
                        />
                        <span className="w-7 text-right font-mono text-[9px] text-gray-500">
                          {(selectedOverlay.anim_duration ?? _overlayDefaultInDur(selectedOverlay.animation ?? 'none')).toFixed(2)}s
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {([
                      { v: 'none',       label: 'None' },
                      { v: 'fade',       label: 'Fade' },
                      { v: 'pop',        label: 'Pop' },
                      { v: 'bounce',     label: 'Bounce' },
                      { v: 'slide-up',   label: '↑ Rise' },
                      { v: 'slide-down', label: '↓ Drop' },
                      { v: 'slide-left', label: '← Slide' },
                      { v: 'slide-right',label: '→ Slide' },
                      { v: 'wipe-up',    label: '↑ Wipe' },
                      { v: 'wipe-down',  label: '↓ Wipe' },
                      { v: 'typewriter', label: 'Type' },
                      { v: 'spin',       label: 'Spin' },
                    ] as const).map(a => {
                      const active = (selectedOverlay.animation ?? 'none') === a.v;
                      return (
                        <button key={a.v}
                          onClick={() => {
                            onUpdateTextOverlay?.(selectedOverlay.id, { animation: a.v });
                            if (videoRef.current) { videoRef.current.currentTime = selectedOverlay.time; setCurrentTime(selectedOverlay.time); }
                          }}
                          className={`rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors ${active ? 'bg-[#2f4941] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                          {a.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* OUT */}
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-2.5 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#94a3b8]">Out</span>
                    {(selectedOverlay.animation_out ?? 'none') !== 'none' && (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="range" min={0.1} max={2} step={0.05}
                          value={selectedOverlay.anim_out_duration ?? 0.35}
                          onChange={e => onUpdateTextOverlay?.(selectedOverlay.id, { anim_out_duration: parseFloat(e.target.value) })}
                          className="w-16 accent-[#94a3b8]"
                        />
                        <span className="w-7 text-right font-mono text-[9px] text-gray-500">
                          {(selectedOverlay.anim_out_duration ?? 0.35).toFixed(2)}s
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {([
                      { v: 'none',       label: 'None' },
                      { v: 'fade',       label: 'Fade' },
                      { v: 'pop',        label: 'Pop' },
                      { v: 'bounce',     label: 'Bounce' },
                      { v: 'slide-up',   label: '↑ Exit' },
                      { v: 'slide-down', label: '↓ Exit' },
                      { v: 'slide-left', label: '← Exit' },
                      { v: 'slide-right',label: '→ Exit' },
                      { v: 'wipe-up',    label: '↑ Wipe' },
                      { v: 'wipe-down',  label: '↓ Wipe' },
                      { v: 'spin',       label: 'Spin' },
                    ] as const).map(a => {
                      const active = (selectedOverlay.animation_out ?? 'none') === a.v;
                      return (
                        <button key={a.v}
                          onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { animation_out: a.v })}
                          className={`rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors ${active ? 'bg-[#94a3b8] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                          {a.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Broadcast the full visual style, size, and position of this overlay to every
                 *  other text overlay so the user can style/place one and bulk-apply.
                 *  Text content and timing stay per-overlay — those are inherently per-element. */}
                {textOverlays.length > 1 && (
                  <button
                    disabled={isApplyingAll}
                    onClick={() => {
                      if (isApplyingAll || applyAllAck !== null) return;
                      setIsApplyingAll(true);
                      const updates: Partial<TextOverlay> = {
                        design: selectedOverlay.design,
                        fontFamily: selectedOverlay.fontFamily,
                        fontSize: selectedOverlay.fontSize,
                        color: selectedOverlay.color,
                        outline: selectedOverlay.outline,
                        outlineColor: selectedOverlay.outlineColor,
                        bold: selectedOverlay.bold,
                        italic: selectedOverlay.italic,
                        backgroundColor: selectedOverlay.backgroundColor,
                        animation: selectedOverlay.animation ?? 'none',
                        animation_out: selectedOverlay.animation_out ?? 'none',
                        anim_duration: selectedOverlay.anim_duration,
                        anim_out_duration: selectedOverlay.anim_out_duration,
                        // Size + position so one caption can define the look and placement for all
                        width: selectedOverlay.width,
                        position: selectedOverlay.position,
                        x_pct: selectedOverlay.x_pct,
                        y_pct: selectedOverlay.y_pct,
                      };
                      // Hold the loading state briefly so the click is visible (the actual
                      // state updates are synchronous and would otherwise flash).
                      window.setTimeout(() => {
                        let applied = 0;
                        textOverlays.forEach(t => {
                          if (t.id !== selectedOverlay.id) {
                            onUpdateTextOverlay?.(t.id, updates);
                            applied += 1;
                          }
                        });
                        setIsApplyingAll(false);
                        setApplyAllAck(applied);
                        window.setTimeout(() => setApplyAllAck(null), 2000);
                      }, 400);
                    }}
                    className={`flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed py-1.5 text-[10px] font-semibold transition-colors disabled:cursor-not-allowed ${
                      applyAllAck !== null
                        ? 'border-emerald-500/50 bg-emerald-50 text-emerald-700'
                        : 'border-[#2f4941]/40 bg-[#2f4941]/5 text-[#2f4941] hover:bg-[#2f4941]/10'
                    }`}
                    title="Copy this overlay's design, font, color, outline, weight, size, position, and animation onto every other text overlay"
                  >
                    {isApplyingAll ? (
                      <>
                        <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                          <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                        </svg>
                        Applying…
                      </>
                    ) : applyAllAck !== null ? (
                      <>
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        Applied to all text
                      </>
                    ) : (
                      <>Apply style to all text</>
                    )}
                  </button>
                )}
              </div>

                {/* Rotation slider */}
                <div>
                  <div className="mb-1 flex items-baseline justify-between">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Rotation</p>
                    <span className="font-mono text-[10px] text-gray-400">{selectedOverlay.rotation ?? 0}°</span>
                  </div>
                  <input
                    type="range"
                    min={0} max={359} step={1}
                    value={selectedOverlay.rotation ?? 0}
                    onChange={e => onUpdateTextOverlay?.(selectedOverlay.id, { rotation: parseInt(e.target.value) })}
                    className="w-full accent-[#2f4941]"
                  />
                  <div className="mt-1 flex gap-1">
                    {[0, 90, 180, 270].map(deg => (
                      <button key={deg} onClick={() => onUpdateTextOverlay?.(selectedOverlay.id, { rotation: deg })} className="flex-1 rounded bg-gray-100 py-0.5 text-[9px] text-gray-500 hover:bg-gray-200">{deg}°</button>
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-[10px] text-gray-400 leading-relaxed">Drag in canvas to reposition · Drag edge handles to resize · Double-click to type</p>
            </div>
          )}

          {/* Captions */}
          <div className="flex-shrink-0 border-b border-gray-100 px-4 py-3.5">
            <div className="flex items-center justify-between mb-2.5">
              <h3 className="text-[12px] font-semibold text-gray-900">Captions</h3>
              <button
                onClick={() => !isAnalyzingCaptions && onCaptionsEnabledChange?.(!captionsEnabled)}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${isAnalyzingCaptions ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${captionsEnabled ? 'bg-[#2f4941]' : 'bg-gray-200'}`}
                role="switch" aria-checked={captionsEnabled}
                disabled={isAnalyzingCaptions}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform duration-200 ${captionsEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
            {isAnalyzingCaptions && (
              <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Analyzing audio...
              </div>
            )}
            {captionsEnabled && !isAnalyzingCaptions && !captionNotice && (
              <div className="rounded-lg bg-brand-primary/10 px-2.5 py-2">
                <p className="text-[10px] leading-snug text-brand-primary">
                  Captions are synced to the audio — fix any wording on the timeline below.
                </p>
              </div>
            )}
            {!isAnalyzingCaptions && captionNotice && (
              <div className="rounded-lg bg-amber-50 px-2.5 py-2">
                <p className="text-[10px] leading-snug text-amber-700">{captionNotice}</p>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── TIMELINE (130px) ── */}
      <div data-tour="timeline" className="flex-shrink-0 border-t border-gray-100 bg-white" style={{ height: '130px' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center border-b border-gray-100 px-4" style={{ height: '36px' }}>
          <div className="flex gap-1">
            <button className="border-b-2 border-[#2f4941] px-3 pb-2.5 pt-2 text-[11px] font-semibold text-[#2f4941]">Timeline</button>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="flex items-center gap-1 text-[10px] text-gray-400"><span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />{timelineSegments.length}</span>
            <span className="flex items-center gap-1 text-[10px] text-gray-400"><span className="inline-block h-2 w-2 rounded-full bg-brand-primary" />{textOverlays.length}</span>
          </div>
        </div>
        <div className="overflow-auto" style={{ height: 'calc(130px - 36px)' }}>
          <UGCVideoTimeline segments={timelineSegments} totalDurationSeconds={duration} showTimeLabels currentPlayheadTime={currentTime} onSeek={(t) => { setCurrentTime(t); if (videoRef.current) videoRef.current.currentTime = t; }} textOverlays={textOverlays} onAddTextOverlay={onAddTextOverlay} onUpdateTextOverlay={onUpdateTextOverlay} onRemoveTextOverlay={onRemoveTextOverlay} onSelectTextOverlay={(id) => { setSelectedOverlayId(id); setSelectedSegmentId(null); const o = textOverlays.find(t => t.id === id); if (o && videoRef.current) { videoRef.current.currentTime = o.time; setCurrentTime(o.time); } }} onSelectSegment={handleSelectSegment} selectedSegmentId={selectedSegmentId} onReorderSegments={onReorderSegments} onUpdateSegment={onUpdateSegment} onRemoveSegment={onRemoveSegment} segmentThumbnails={thumbnailById} segmentVideoUrls={segmentVideoUrls} />
        </div>
      </div>

      {/* Hidden preview audio for music panel */}
      <audio ref={previewAudioRef} style={{ display: 'none' }} />

      {/* Guided walkthrough */}
      {showHelpTour && (
        <UGCEditorHelpTour steps={EDITOR_TOUR_STEPS} rootRef={editorRootRef} onClose={() => setShowHelpTour(false)} />
      )}

      {/* ── MEDIA MODAL ── */}
      {showMediaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowMediaModal(false)}>
          <div className="relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" style={{ width: '700px', height: '540px' }} onClick={e => e.stopPropagation()}>
            <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 px-5 py-3.5">
              <h2 className="text-sm font-semibold text-gray-900">Add Media</h2>
              <button onClick={() => setShowMediaModal(false)} className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors text-xl leading-none">×</button>
            </div>
            <div className="flex flex-shrink-0 border-b border-gray-100 px-5 gap-1">
              {([
                { id: 'templates', label: 'Text Templates' },
                { id: 'library', label: 'My Library' },
                { id: 'videos', label: 'Pexels Videos' },
                { id: 'upload', label: 'Upload' },
                { id: 'ai', label: 'AI Generate' },
                { id: 'music', label: 'Music' },
              ] as const).map(tab => (
                <button key={tab.id} onClick={() => setMediaModalTab(tab.id)} className={`border-b-2 px-4 py-2.5 text-[11px] font-medium transition-colors ${mediaModalTab === tab.id ? 'border-[#2f4941] text-[#2f4941]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{tab.label}</button>
              ))}
            </div>
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {mediaModalTab === 'templates' && <UGCTextTemplateGallery onSelect={handleApplyTextTemplate} />}
              {mediaModalTab === 'library' && <UGCAddImagesStep controlledTab="stock" videoFormat={videoFormat} customerId={customerId} idToken={idToken} selectedMediaIds={selectedMediaIds} onToggleMedia={handleAddMediaFromModal} onClearAllMedia={onClearAllMedia ?? (() => {})} onMediaLoaded={onMediaLoaded ?? (() => {})} onVideoMediaImported={onVideoMediaImported} thumbnailById={thumbnailById} enableLibraryDrag={enableLibraryDrag} scriptContent={scriptContent} agentTools={agentTools} />}
              {mediaModalTab === 'videos' && <UGCAddImagesStep controlledTab="pexels" videoFormat={videoFormat} customerId={customerId} idToken={idToken} selectedMediaIds={selectedMediaIds} onToggleMedia={handleAddMediaFromModal} onClearAllMedia={onClearAllMedia ?? (() => {})} onMediaLoaded={onMediaLoaded ?? (() => {})} onVideoMediaImported={onVideoMediaImported} thumbnailById={thumbnailById} enableLibraryDrag={enableLibraryDrag} scriptContent={scriptContent} agentTools={agentTools} />}
              {mediaModalTab === 'upload' && <UGCAddImagesStep controlledTab="upload" videoFormat={videoFormat} customerId={customerId} idToken={idToken} selectedMediaIds={selectedMediaIds} onToggleMedia={handleAddMediaFromModal} onClearAllMedia={onClearAllMedia ?? (() => {})} onMediaLoaded={onMediaLoaded ?? (() => {})} onVideoMediaImported={onVideoMediaImported} thumbnailById={thumbnailById} enableLibraryDrag={enableLibraryDrag} scriptContent={scriptContent} agentTools={agentTools} />}
              {mediaModalTab === 'ai' && <UGCAddImagesStep controlledTab="generate" videoFormat={videoFormat} customerId={customerId} idToken={idToken} selectedMediaIds={selectedMediaIds} onToggleMedia={handleAddMediaFromModal} onClearAllMedia={onClearAllMedia ?? (() => {})} onMediaLoaded={onMediaLoaded ?? (() => {})} onVideoMediaImported={onVideoMediaImported} thumbnailById={thumbnailById} enableLibraryDrag={enableLibraryDrag} scriptContent={scriptContent} agentTools={agentTools} />}
              {mediaModalTab === 'music' && (
                <div className="overflow-y-auto p-5 space-y-4" style={{ height: '100%' }}>
                  <p className="text-[11px] text-gray-400">Music is off by default — pick a track below to add it to your video.</p>
                  <div className="space-y-1.5">
                    {UGC_BACKGROUND_MUSIC_PRESETS.map(preset => {
                      const active = musicPresetId === preset.id;
                      const isPreviewing = previewingPresetId === preset.id;
                      const canPreview = !!preset.url;
                      const handlePreview = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        const audio = previewAudioRef.current;
                        if (!audio) return;
                        if (isPreviewing) {
                          audio.pause();
                          setPreviewingPresetId(null);
                          setPreviewLoadingId(null);
                        } else {
                          audio.src = preset.url;
                          audio.volume = Math.min(1, Math.max(0, (musicVolumePercent ?? 30) / 100));
                          audio.currentTime = 0;
                          audio.loop = true;
                          setPreviewLoadingId(preset.id);
                          setPreviewingPresetId(preset.id);
                          const onCanPlay = () => {
                            setPreviewLoadingId(null);
                            audio.removeEventListener('canplay', onCanPlay);
                          };
                          audio.addEventListener('canplay', onCanPlay);
                          audio.play().catch(() => {
                            setPreviewLoadingId(null);
                            setPreviewingPresetId(null);
                          });
                        }
                      };
                      return (
                        <div key={preset.id} className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-3 transition-all ${active ? 'bg-[#2f4941]/10 ring-1 ring-[#2f4941]' : 'border border-gray-100 hover:bg-gray-50'}`}>
                          {canPreview ? (
                            <button onClick={handlePreview} disabled={previewLoadingId === preset.id && !isPreviewing} className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors ${isPreviewing ? 'bg-[#2f4941] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                              {previewLoadingId === preset.id
                                ? <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                : isPreviewing
                                  ? <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                                  : <svg className="h-3.5 w-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>}
                            </button>
                          ) : (
                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 text-sm">✕</span>
                          )}
                          <button className="flex-1 text-left" onClick={() => { onMusicPresetChange?.(preset.id); previewAudioRef.current?.pause(); setPreviewingPresetId(null); }}>
                            <span className={`text-[12px] font-medium ${active ? 'text-[#2f4941]' : 'text-gray-700'}`}>{preset.title}</span>
                          </button>
                          {active ? (
                            <span className="flex-shrink-0 flex items-center gap-1 rounded-lg bg-[#2f4941]/15 px-2 py-1 text-[10px] font-semibold text-[#2f4941]">
                              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                              Added
                            </span>
                          ) : (
                            <button
                              onClick={() => { onMusicPresetChange?.(preset.id); previewAudioRef.current?.pause(); setPreviewingPresetId(null); }}
                              className="flex-shrink-0 rounded-lg border border-gray-200 px-2.5 py-1 text-[10px] font-semibold text-gray-500 hover:border-[#2f4941] hover:text-[#2f4941] transition-colors"
                            >
                              Add
                            </button>
                          )}
                        </div>
                      );
                    })}
                    <div className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-3 border border-dashed transition-all ${musicPresetId === UGC_MUSIC_PRESET_CUSTOM ? 'bg-[#2f4941]/10 border-[#2f4941]' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm ${musicPresetId === UGC_MUSIC_PRESET_CUSTOM ? 'bg-[#2f4941] text-white' : 'bg-gray-100 text-gray-400'}`}>🔗</span>
                      <button className="flex-1 text-left" onClick={() => onMusicPresetChange?.(UGC_MUSIC_PRESET_CUSTOM)}>
                        <span className={`text-[12px] font-medium ${musicPresetId === UGC_MUSIC_PRESET_CUSTOM ? 'text-[#2f4941]' : 'text-gray-500'}`}>Custom URL</span>
                      </button>
                      {musicPresetId === UGC_MUSIC_PRESET_CUSTOM ? (
                        <span className="flex-shrink-0 flex items-center gap-1 rounded-lg bg-[#2f4941]/15 px-2 py-1 text-[10px] font-semibold text-[#2f4941]">
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                          Added
                        </span>
                      ) : (
                        <button onClick={() => onMusicPresetChange?.(UGC_MUSIC_PRESET_CUSTOM)} className="flex-shrink-0 rounded-lg border border-gray-200 px-2.5 py-1 text-[10px] font-semibold text-gray-500 hover:border-[#2f4941] hover:text-[#2f4941] transition-colors">Add</button>
                      )}
                    </div>
                  </div>
                  {musicPresetId === UGC_MUSIC_PRESET_CUSTOM && <input type="url" placeholder="https://…" value={customMusicUrl} onChange={e => onCustomMusicUrlChange?.(e.target.value)} className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-[12px] outline-none focus:border-[#2f4941] focus:bg-white" />}
                  <div className="pt-2">
                    <div className="mb-2 flex items-center justify-between"><p className="text-[11px] font-semibold text-gray-700">Volume</p><span className="text-[11px] font-mono text-gray-500">{musicVolumePercent}%</span></div>
                    <input type="range" min={0} max={100} value={musicVolumePercent} onChange={e => onMusicVolumeChange?.(Number(e.target.value))} className="w-full" style={{ accentColor: '#2f4941' }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UGCVideoEditor;
